/* GENERATED from frontend/js by tools/build-js.mjs. Edit those files, then run npm run build. */
/* ── 00-core.js ── */
/*
 * Core runtime: helpers, attribution and email-marketing identity.
 * Ported from src/lib/{tracking,klaviyo-onsite}.ts. The theme loads no ad
 * pixels: WeTracked reads Shopify's standard customer events instead.
 */
(function () {
  const LV = (window.Lavendr = window.Lavendr || {});

  /* ── Small helpers ─────────────────────────────────────────────── */

  // Desktop page zoom (tools/zoom-css.mjs): CSS breakpoints from 1024px up
  // are scaled by this factor, so JS width checks scale the same way.
  LV.zoomScale = () =>
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--zoom-scale")) || 1;
  LV.isDesktop = () => window.innerWidth >= 1024 * LV.zoomScale();

  LV.eventId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  LV.safeStorage = (kind) => {
    try {
      const store = window[kind];
      const probe = "__lv";
      store.setItem(probe, "1");
      store.removeItem(probe);
      return store;
    } catch {
      return null;
    }
  };
  const local = LV.safeStorage("localStorage");
  const session = LV.safeStorage("sessionStorage");
  LV.local = local;
  LV.session = session;

  LV.getCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  };
  LV.setCookie = (name, value, days) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
  };

  // Shopify's format for the active currency, read from Liquid's rendering of
  // 12345.67 ("$12,345.67", "12 345,67 kr", "€12.345,67"), so amounts written
  // by JS look exactly like the ones Liquid printed next to them.
  const sample = /^(.*?)12(\D?)345(\D)67(.*)$/s.exec(String(LV.moneySample || ""));
  const liquidMoney = (cents) => {
    const [, prefix, group, decimal, suffix] = sample;
    const rounded = Math.round(Number(cents || 0));
    const whole = String(Math.floor(Math.abs(rounded) / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, group);
    const fraction = Math.abs(rounded) % 100;
    // money_without_trailing_zeros: drop ".00", keep ".95".
    const amount = fraction ? `${whole}${decimal}${String(fraction).padStart(2, "0")}` : whole;
    return `${rounded < 0 ? "-" : ""}${prefix}${amount}${suffix}`;
  };

  /** Like Liquid's money_without_trailing_zeros: "$48", "€4,95", "486 kr". Takes cents. */
  LV.money = (cents, currency) => {
    const code = currency || LV.currency || "USD";
    if (sample && code === LV.currency) return liquidMoney(cents);
    const amount = Math.round(Number(cents || 0)) / 100;
    const decimals = Number.isInteger(amount) ? 0 : 2;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  };

  /** Converts a store-currency amount (e.g. a $20 gift value) to the active currency, in cents. */
  LV.localCents = (storeAmount) => {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    const converted = Number(storeAmount) * rate;
    // Whole amounts, like the fixed regional price table on the old site.
    return Math.round(rate === 1 ? converted * 100 : Math.round(converted) * 100);
  };

  /**
   * Converts a store-currency compare-at price (cents) and ends it like the
   * local price it sits next to (e.g. €107.95 beside €71.95), as the old
   * site's localComparePrice() did, so every saving is a whole amount.
   */
  LV.localCompareCents = (storeCents, localPriceCents) => {
    const whole = Math.round((Number(storeCents) / 100) * Number(window.Shopify?.currency?.rate || 1));
    const ending = Math.round(Number(localPriceCents)) % 100;
    return ending ? (whole - 1) * 100 + ending : whole * 100;
  };

  LV.on = (event, handler) => document.addEventListener(event, handler);
  LV.emit = (event, detail) =>
    document.dispatchEvent(new CustomEvent(event, { detail }));

  LV.prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ── Attribution (UTMs / click ids) ────────────────────────────── */

  const ATTRIBUTION_KEY = "lavendr-attribution";
  const TRACKED_PARAMS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "utm_id",
    "fbclid",
    "gclid",
    "wbraid",
    "gbraid",
    "ttclid",
    "msclkid",
    "twclid",
    "tw_source",
    "tw_adid",
  ];
  const UTM_PARAMS = TRACKED_PARAMS.filter((key) => key.startsWith("utm_") && key !== "utm_id");

  function readAttribution() {
    try {
      const parsed = JSON.parse(local?.getItem(ATTRIBUTION_KEY) || "null");
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }

  /** Last-touch params; landing page and referrer kept from the first visit. */
  function captureAttribution() {
    const params = new URLSearchParams(window.location.search);

    const fbclid = params.get("fbclid");
    if (fbclid) LV.setCookie("_fbc", `fb.1.${Date.now()}.${fbclid}`, 90);
    const utm = {};
    for (const key of UTM_PARAMS) {
      const value = params.get(key);
      if (value) utm[key] = value;
    }
    if (Object.keys(utm).length) LV.setCookie("_utm", JSON.stringify(utm), 90);

    if (!local) return;
    const incoming = {};
    for (const key of TRACKED_PARAMS) {
      const value = params.get(key);
      if (value) incoming[key] = value.slice(0, 250);
    }
    const existing = readAttribution();
    if (!Object.keys(incoming).length && existing) return;
    local.setItem(
      ATTRIBUTION_KEY,
      JSON.stringify({
        params: Object.keys(incoming).length ? incoming : existing?.params ?? {},
        landingPage: existing?.landingPage ?? window.location.href.slice(0, 500),
        referrer: existing?.referrer ?? document.referrer.slice(0, 500),
        updatedAt: Date.now(),
      }),
    );
  }

  LV.getAttribution = () => {
    const stored = readAttribution();
    if (!stored) return {};
    const flat = { ...stored.params };
    if (stored.landingPage) flat.landing_page = stored.landingPage;
    if (stored.referrer) flat.referrer = stored.referrer;
    return flat;
  };

  captureAttribution();

  /**
   * Ties this browser to the shopper's email-marketing contact. Omnisend's
   * app embed defines window.omnisend; Klaviyo's embed (window.klaviyo) is
   * only called while it is still switched on during the migration.
   */
  LV.identify = (email) => {
    const clean = String(email || "").trim().toLowerCase();
    if (!clean) return;
    try {
      if (typeof window.omnisend?.identifyContact === "function") {
        window.omnisend.identifyContact({ email: clean });
      }
    } catch {
      /* best effort */
    }
    try {
      if (typeof window.klaviyo?.identify === "function") window.klaviyo.identify({ email: clean });
    } catch {
      /* best effort */
    }
  };

  /**
   * Custom analytics events are off: WeTracked reads Shopify's standard
   * customer events, so the theme sends nothing of its own. Kept as a no-op
   * so components that still call it (popup, cart upgrades, quiz) are safe.
   */
  LV.track = () => {};

  /* ── Standard commerce events ──────────────────────────────────── */

  // Shopify emits page_viewed, product_viewed, product_added_to_cart and
  // checkout_started itself, and WeTracked (a Customer Events pixel) sends
  // them on. Omnisend's app embed tracks product pages and the cart on its
  // own. The theme fires no events of its own, so nothing counts twice.
  LV.events = {
    viewContent() {},
    addToCart() {},
    async initiateCheckout() {},
    lead(email) {
      LV.identify(email);
    },
  };

  /* ── Newsletter signup (Shopify customer form, no backend) ─────── */

  // Shopify tag rules: no commas, at most 255 characters.
  const toTag = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/[,\s]+/g, "-")
      .slice(0, 80);

  /**
   * Subscribes an email through Shopify's newsletter form (form_type
   * "customer"). Shopify creates or updates the customer with email
   * marketing consent, and the Omnisend app syncs that customer, consent
   * and tags into Omnisend. The form is posted with fetch so the page does
   * not reload.
   *
   * When spam protection is on, Shopify guards customer forms with hCaptcha
   * and a bare POST is rejected ("Missing CAPTCHA token"). Shopify.captcha.protect() binds the
   * form, solves the (usually invisible) captcha on submit, adds the
   * h-captcha-response field and then calls form.submit(). That submit is
   * overridden below to send the form with fetch instead of navigating.
   *
   * `tags` become Shopify customer tags, e.g. "source:email-popup".
   */
  LV.subscribe = (email, { source = "website", tags = [] } = {}) =>
    new Promise((resolve, reject) => {
      const form = document.createElement("form");
      form.method = "post";
      form.action = "/contact#lv-newsletter";
      form.acceptCharset = "UTF-8";
      form.setAttribute("aria-hidden", "true");
      form.style.cssText = "position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;";
      const allTags = ["newsletter", `source:${toTag(source)}`, ...tags.map(toTag)].filter(
        (tag, i, list) => tag && !tag.endsWith(":") && list.indexOf(tag) === i,
      );
      const fields = {
        form_type: "customer",
        utf8: "\u2713",
        "contact[email]": email.trim(),
        "contact[tags]": allTags.join(", "),
      };
      for (const [name, value] of Object.entries(fields)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.append(input);
      }

      let settled = false;
      let waitForCaptcha;
      let giveUp;
      const finish = (err) => {
        if (settled) return;
        settled = true;
        clearInterval(waitForCaptcha);
        clearTimeout(giveUp);
        form.remove();
        if (err) reject(err);
        else resolve();
      };
      const failed = () => new Error("Failed to subscribe. Please try again.");

      const send = async () => {
        if (settled) return; // a captcha solved after we already gave up
        clearTimeout(giveUp);
        try {
          const res = await fetch("/contact", {
            method: "POST",
            body: new FormData(form),
            headers: { Accept: "text/html" },
          });
          // Only a redirect to ...?customer_posted=true means Shopify saved
          // the customer. A form error re-renders the page as a plain 200, a
          // missing or failed captcha comes back as 400 or a /challenge page.
          if (!res.ok || !/[?&]customer_posted=true/.test(res.url)) throw failed();
          finish();
        } catch (err) {
          finish(err instanceof Error ? err : failed());
        }
      };
      form.submit = send;
      document.body.append(form);

      const protect = window.Shopify?.captcha?.protect;
      if (typeof protect !== "function") {
        // Bot protection is off for this store: post straight away.
        void send();
        return;
      }
      try {
        protect(form);
      } catch {
        void send();
        return;
      }
      // Binding loads hCaptcha first, then replaces form.submit with its own
      // captcha-then-submit function. Wait for that, then submit. Shopify's
      // script swallows a closed or failed challenge (it only logs it), so
      // the timers below are the only way out: 15s for hCaptcha to load,
      // then 60s to pass the captcha (enough for a visible puzzle).
      waitForCaptcha = setInterval(() => {
        if (form.submit !== send) {
          clearInterval(waitForCaptcha);
          clearTimeout(giveUp);
          giveUp = setTimeout(() => finish(failed()), 60000);
          form.submit();
        }
      }, 100);
      giveUp = setTimeout(() => finish(failed()), 15000);
    });

  /* ── Store-currency amounts in other markets ──────────────────── */

  // Liquid converts variant prices to the shopper's currency, but not fixed
  // amounts such as the $20 guide value. Those render with
  // data-store-money="<store cents>" and are converted here.
  if (Number(window.Shopify?.currency?.rate || 1) !== 1) {
    document.querySelectorAll("[data-store-money]").forEach((el) => {
      el.textContent = LV.money(LV.localCents(Number(el.getAttribute("data-store-money")) / 100));
    });
  }

  /**
   * "$140" in marketing copy -> the active currency, like localizeCopy() in
   * the old React components (same "$72 billion" exclusion as lp13/lp16).
   * Elements opt in with data-local-copy="<original copy>", so re-running is safe.
   */
  LV.localizeCopy = (text) =>
    String(text).replace(/\$([\d,]+(?:\.\d+)?)(?!\s*billion)/g, (_, value) =>
      LV.money(LV.localCents(Number(value.replaceAll(",", "")))),
    );
  LV.localizeCopyIn = (root = document) => {
    root.querySelectorAll("[data-local-copy]").forEach((el) => {
      const next = LV.localizeCopy(el.getAttribute("data-local-copy"));
      if (el.textContent !== next) el.textContent = next;
    });
  };
  if (Number(window.Shopify?.currency?.rate || 1) !== 1) LV.localizeCopyIn(document);

  /*
   * Compare-at prices and savings in other markets. Shopify drops compare-at
   * prices when it converts currency, and Liquid cannot convert the gift
   * values, so Liquid's numbers there are placeholders. This rebuilds them
   * the way the old site did: store compare-at (theme setting) converted and
   * ended like the local price, gifts converted to whole amounts. It runs
   * before the cart, product and quiz scripts read LV.catalog / the DOM.
   */
  if (Number(window.Shopify?.currency?.rate || 1) !== 1) {
    const gifts = LV.gifts || {};
    const giftCents = (cans) =>
      LV.localCents(gifts.guideValue || 0) +
      (cans > 1 ? LV.localCents(gifts.shippingValue || 0) : 0) +
      (cans === 3
        ? LV.localCents(gifts.frotherValue || 0) + LV.localCents(gifts.shakerValue || 0) + LV.localCents(gifts.sachetsValue || 0)
        : 0);
    const byCans = {};
    Object.values(LV.catalog || {}).forEach((entry) => {
      if (entry.storeCompareAt > 0) entry.compareAt = LV.localCompareCents(entry.storeCompareAt, entry.price);
      if (!entry.once) byCans[entry.cans] = entry; // one-time prices are plain Liquid money
    });
    const savingsCents = (entry) => Math.max(0, entry.compareAt - entry.price) + giftCents(entry.cans);
    const each = (attr, render) =>
      document.querySelectorAll(`[${attr}]`).forEach((el) => {
        const entry = byCans[el.getAttribute(attr)];
        if (entry) el.textContent = LV.money(render(entry));
      });
    each("data-lv-compare", (entry) => entry.compareAt);
    each("data-bag-total", (entry) => entry.price);
    each("data-bag-each", (entry) => Math.floor(entry.price / entry.cans));
    each("data-lv-savings", savingsCents);
    each("data-lv-was", (entry) => entry.price + savingsCents(entry));

    // "Save up to" = the Enthusiast Box's savings, gifts included.
    const top = byCans[3];
    LV.maxSavingsCents = top
      ? savingsCents(top)
      : (LV.maxSavings?.discountCents || 0) + LV.localCents((LV.maxSavings?.giftStoreCents || 0) / 100);
    const amount = LV.money(LV.maxSavingsCents);
    document.querySelectorAll("[data-max-savings]").forEach((el) => {
      el.textContent = amount;
    });
    document.querySelectorAll("[data-max-savings-aria]").forEach((el) => {
      el.setAttribute("aria-label", el.getAttribute("data-max-savings-aria").replace("{amount}", amount));
    });
    document.querySelectorAll("[data-quiz][data-savings]").forEach((el) => {
      el.setAttribute("data-savings", amount);
    });
  }

  /* ── Page-wide behaviour from SiteChrome.tsx ──────────────────── */

  /** Smooth scroll for [data-scroll-to="#id"] (respects reduced motion). */
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-scroll-to]");
    if (!trigger) return;
    const target = document.querySelector(trigger.getAttribute("data-scroll-to"));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({
      behavior: LV.prefersReducedMotion() ? "auto" : "smooth",
      block: trigger.getAttribute("data-scroll-block") || "start",
    });
  });
})();

/* ── 10-cart.js ── */
/*
 * Cart: Shopify AJAX cart API + the slide-in drawer.
 * Ported from src/components/SliderCart.tsx and src/lib/cart-gifts.ts.
 *
 * Differences from the old React cart, on purpose:
 * - The cart lives in Shopify (/cart.js), so it follows the shopper to
 *   checkout and shows in Shopify's abandoned-cart data.
 * - Shipping protection is a real cart line ($3 Package Protection) with the Kaching
 *   plan on exactly the bundle's billing schedule, so both renew in one
 *   subscription. The toggle adds/removes that line.
 * - Gifts stay display-only lines, exactly as before (fulfillment packs them
 *   from the bundle variant).
 * - UTMs, click ids and the landing page are written to cart attributes, so
 *   every order carries them.
 */
(function () {
  const LV = window.Lavendr;
  const routes = LV.routes || {};
  const PROTECTION_KEY = "lavendr-protection";
  const SERVINGS_PER_CAN = 20;

  /* ── API ──────────────────────────────────────────────────────── */

  async function request(url, body) {
    const res = await fetch(url, {
      method: body ? "POST" : "GET",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.description || data.message || "Cart request failed");
    }
    return data;
  }

  const api = {
    get: () => request(`${routes.cart || "/cart"}.js`),
    add: (items) => request(`${routes.cartAdd || "/cart/add"}.js`, { items }),
    change: (key, quantity) =>
      request(`${routes.cartChange || "/cart/change"}.js`, { id: key, quantity }),
    update: (payload) => request(`${routes.cartUpdate || "/cart/update"}.js`, payload),
    clear: () => request(`${routes.cartClear || "/cart/clear"}.js`, {}),
  };
  LV.cartApi = api;

  /* ── Catalog helpers ──────────────────────────────────────────── */

  const protection = LV.protection;
  const protectionVariant = protection?.variants?.[0] || null;
  const protectionPlans = (protection?.selling_plan_groups || []).flatMap(
    (group) => group.selling_plans || [],
  );

  const isProtectionLine = (line) =>
    protectionVariant && line.variant_id === protectionVariant.id;
  const bundleFor = (line) => LV.catalog?.[String(line.variant_id)] || null;

  /** Kaching config of the bundle products' plans, by plan id (snippets/plan-configs-json.liquid). */
  const planConfigs = LV.planConfigs || {};

  /**
   * A Kaching plan's real billing and delivery settings, from the "config"
   * option Kaching stores on every plan. Plan names are only labels: the
   * protection plan named "Every 30 Days" was billed monthly. Takes a full
   * plan or just { id } (then the config comes from planConfigs).
   */
  function scheduleOf(plan) {
    const config =
      plan?.options?.find((option) => option.name === "config")?.value ?? planConfigs[plan?.id];
    try {
      const { billing, delivery } = JSON.parse(config);
      return billing && delivery ? JSON.stringify({ billing, delivery }) : null;
    } catch {
      return null;
    }
  }

  /**
   * The protection plan on exactly the bundle's schedule. Shopify puts
   * subscription lines in one contract only when billing and delivery match,
   * so a near miss (30 days vs 1 month) gave the shopper a second subscription
   * for the protection alone. No exact match: protection is a one-time charge.
   */
  function protectionPlanForPlan(bundlePlan) {
    const own = bundlePlan?.id;
    const same = own && protectionPlans.find((plan) => plan.id === own);
    if (same) return same;
    const schedule = scheduleOf(bundlePlan);
    if (!schedule) return null;
    return protectionPlans.find((plan) => scheduleOf(plan) === schedule) || null;
  }
  const protectionPlanFor = (line) => protectionPlanForPlan(line?.selling_plan_allocation?.selling_plan);

  const protectionItem = (planId) => ({
    id: protectionVariant.id,
    quantity: 1,
    ...(planId ? { selling_plan: planId } : {}),
  });

  // Kept in memory too, so the toggle still works when storage is blocked.
  let protectionChoice = null;
  function protectionWanted() {
    const stored = protectionChoice ?? LV.local?.getItem(PROTECTION_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    return LV.protectionDefaultOn !== false;
  }
  function setProtectionWanted(on) {
    protectionChoice = on ? "1" : "0";
    LV.local?.setItem(PROTECTION_KEY, protectionChoice);
  }

  /** Display-only gifts, same rules as getCartGiftLines() for the live offer. */
  function giftsFor(bundle) {
    // One-time purchases (Subscribe & Save off) come without gifts.
    if (!bundle || bundle.once) return [];
    const gifts = [
      { key: "guide", name: "Recipe Book", image: LV.gifts.guideImage, value: LV.gifts.guideValue },
    ];
    if (bundle.cans > 1) {
      gifts.push({ key: "shipping", name: "Free Shipping", image: LV.gifts.shippingImage, value: LV.gifts.shippingValue });
    }
    if (bundle.cans === 3) {
      gifts.push({ key: "frother", name: "Magic Frother", image: LV.gifts.frotherImage, value: LV.gifts.frotherValue });
      gifts.push({ key: "shaker", name: "Premium Shaker", subtitle: "Delivered on your 2nd order", image: LV.gifts.shakerImage, value: LV.gifts.shakerValue });
      gifts.push({ key: "sachets", name: "5 Travel Sachets", subtitle: "Delivered on your 2nd order", image: LV.gifts.sachetsImage, value: LV.gifts.sachetsValue });
    }
    return gifts;
  }

  function lineSavings(line, bundle) {
    if (!bundle) return 0;
    const unit = line.final_price ?? line.price;
    const blend = Math.max(0, bundle.compareAt - unit) * line.quantity;
    if (bundle.once) return blend;
    const guide = LV.localCents(LV.gifts.guideValue) * line.quantity;
    const shipping = bundle.freeShipping ? LV.localCents(LV.gifts.shippingValue) : 0;
    const vip =
      bundle.cans === 3
        ? (LV.localCents(LV.gifts.frotherValue) + LV.localCents(LV.gifts.shakerValue || 0) + LV.localCents(LV.gifts.sachetsValue || 0)) *
          line.quantity
        : 0;
    return blend + guide + shipping + vip;
  }

  /* ── State ────────────────────────────────────────────────────── */

  let cart = null;
  let busy = false;
  let task = Promise.resolve(); // the cart write in flight, if any
  let loading = Promise.resolve(); // the last background cart read

  /*
   * Optimistic drawer state while a write is in flight, so taps show at once
   * instead of after the Shopify round trips: the protection toggle's new
   * position, lines being removed, and a bundle's new can count.
   */
  let protectionPending = null;
  const removing = new Set();
  const stepping = new Map(); // line key -> can count shown

  function setCart(next) {
    cart = next;
    LV.cart = next;
    render();
    LV.emit("lavendr:cart", next);
  }

  async function refresh() {
    setCart(await api.get());
    return cart;
  }

  /**
   * Runs one cart write at a time (parallel writes to a Shopify cart race).
   * A tap while one runs is dropped, except the protection toggle, which is
   * queued and re-synced afterwards. Clears the optimistic state when done.
   */
  let protectionQueued = false;
  async function lock(fn) {
    if (busy) return false;
    busy = true;
    let release;
    task = new Promise((resolve) => (release = resolve));
    drawer?.setAttribute("aria-busy", "true");
    try {
      await fn();
    } finally {
      busy = false;
      release();
      drawer?.removeAttribute("aria-busy");
      if (protectionQueued) {
        syncProtectionSoon();
      } else {
        protectionPending = null;
        removing.clear();
        stepping.clear();
        if (cart) render();
      }
    }
    return true;
  }

  function guarded(fn) {
    return lock(async () => {
      try {
        await fn();
      } catch (err) {
        console.error(err);
        await refresh().catch(() => {});
      }
    });
  }

  function syncProtectionSoon() {
    if (busy) {
      protectionQueued = true;
      return;
    }
    protectionQueued = false;
    guarded(syncProtection);
  }

  /**
   * Keeps exactly one protection line, on the right plan, when wanted. A no-op
   * (no requests) when the cart is already right, which is the usual case.
   */
  async function syncProtection() {
    if (!protectionVariant || !cart) return;
    const products = cart.items.filter((line) => !isProtectionLine(line));
    const existing = cart.items.filter(isProtectionLine);
    const want = protectionWanted() && products.length > 0;
    const planId = protectionPlanFor(products[0])?.id ?? null;

    const correct =
      existing.length === 1 &&
      existing[0].quantity === 1 &&
      (existing[0].selling_plan_allocation?.selling_plan?.id ?? null) === planId;
    if (want && correct) return;
    if (!want && existing.length === 0) return;

    if (existing.length) {
      // update.js answers with the whole cart, so a removal needs no reload.
      const next = await api.update({
        updates: Object.fromEntries(existing.map((line) => [line.key, 0])),
      });
      if (!want) return setCart(next);
    }
    await api.add([protectionItem(planId)]);
    await refresh();
  }

  /** Writes stored UTMs / click ids onto the cart so they land on the order. */
  async function syncAttributes() {
    const attribution = LV.getAttribution();
    if (!Object.keys(attribution).length || !cart) return;
    const current = cart.attributes || {};
    const changed = Object.entries(attribution).some(([key, value]) => current[key] !== value);
    if (!changed) return;
    try {
      const next = await api.update({ attributes: attribution });
      cart = next;
      LV.cart = next;
    } catch {
      /* attribution is best-effort */
    }
  }

  /**
   * Replaces the cart with one bundle (the old replaceCart + openCart flow
   * the product page used) and opens the drawer.
   */
  LV.addBundle = async ({ variantId, sellingPlanId, quantity = 1, name, price }) => {
    // A tap right after page load or a tab switch waits for that cart read.
    await loading;
    while (busy) await task;
    await lock(async () => {
      try {
        // An empty cart needs no clear: saves a round trip on a first add.
        if (!cart || cart.items.length) await api.clear();
        const item = { id: variantId, quantity, ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}) };
        const items = [item];
        // Protection goes in the same request, already on the bundle's plan.
        if (protectionVariant && protectionWanted()) {
          const planId = protectionPlanForPlan(sellingPlanId ? { id: sellingPlanId } : null)?.id ?? null;
          items.push(protectionItem(planId));
        }
        try {
          await api.add(items);
        } catch (err) {
          if (items.length === 1) throw err;
          // Protection refused: add the bundle alone, unless it went in.
          await refresh();
          if (!cart.items.some((line) => line.variant_id === variantId)) await api.add([item]);
        }
        await refresh();
        LV.events.addToCart({ variantId, sellingPlanId, quantity, name, price });
        open();
      } catch (err) {
        console.error("Add to cart failed:", err);
        alert("Sorry, we could not add that to your bag. Please try again.");
        await refresh().catch(() => {});
        return;
      }
      // Normally no-ops; the drawer is already open.
      try {
        await syncProtection();
        await syncAttributes();
      } catch (err) {
        console.error(err);
        await refresh().catch(() => {});
      }
    });
  };

  /** Adds without clearing (generic add-to-cart buttons). */
  LV.addToCart = async ({ variantId, sellingPlanId, quantity = 1, name, price }) => {
    await api.add([{ id: variantId, quantity, ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}) }]);
    await refresh();
    await syncProtection();
    await syncAttributes();
    LV.events.addToCart({ variantId, sellingPlanId, quantity, name, price });
    open();
  };

  /* ── Drawer ───────────────────────────────────────────────────── */

  const drawer = document.querySelector("[data-cart-drawer]");
  const backdrop = document.querySelector("[data-cart-backdrop]");
  const $ = (selector) => drawer?.querySelector(selector);
  let timer = null;
  let secondsLeft = 0;
  let isOpen = false;

  function setOpen(next) {
    if (!drawer) return;
    isOpen = next;
    drawer.classList.toggle("translate-x-0", next);
    drawer.classList.toggle("translate-x-full", !next);
    backdrop?.classList.toggle("opacity-100", next);
    backdrop?.classList.toggle("opacity-0", !next);
    backdrop?.classList.toggle("pointer-events-none", !next);
    if (next) drawer.removeAttribute("inert");
    else drawer.setAttribute("inert", "");
    document.body.style.overflow = next ? "hidden" : "";
    LV.emit("lavendr:cart-open", next);
    clearInterval(timer);
    if (next) {
      secondsLeft = (LV.reservationMinutes || 10) * 60;
      renderTimer();
      timer = setInterval(() => {
        secondsLeft = Math.max(0, secondsLeft - 1);
        renderTimer();
      }, 1000);
    }
  }
  function open() {
    setOpen(true);
  }
  function close() {
    setOpen(false);
  }
  LV.openCart = open;
  LV.closeCart = close;
  LV.isCartOpen = () => isOpen;

  function renderTimer() {
    const mm = $("[data-cart-timer-mm]");
    const ss = $("[data-cart-timer-ss]");
    if (mm) mm.textContent = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
    if (ss) ss.textContent = String(secondsLeft % 60).padStart(2, "0");
  }

  const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);

  const TRASH_ICON = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>`;

  function cartLine({ key, image, name, subtitle, quantity, locked, increaseDisabled, removable, price }) {
    const qtyButton =
      "flex h-5 w-5 items-center justify-center rounded-full text-[#2d2e2d]/60 hover:bg-black/[.06] hover:text-[#2d2e2d] transition text-base leading-none disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";
    return `
      <div class="flex gap-3">
        <div class="h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl bg-[#fdf6f7]">
          <img src="${escapeHtml(image)}" alt="${escapeHtml(name)}" width="144" height="144" loading="lazy" class="h-full w-full object-cover" style="color:transparent">
        </div>
        <div class="flex flex-1 flex-col justify-between">
          <div class="flex items-start justify-between">
            <div>
              <h3 class="text-sm font-normal text-black leading-tight">${escapeHtml(name)}</h3>
              ${subtitle ? `<p class="text-xs text-foreground/50 leading-snug">${escapeHtml(subtitle)}</p>` : ""}
            </div>
            ${removable ? `<button type="button" data-cart-remove="${key}" class="ml-2 -mt-1 rounded-lg p-1 text-foreground/40 hover:bg-black/[.04] hover:text-red-500" aria-label="Remove ${escapeHtml(name)}">${TRASH_ICON}</button>` : ""}
          </div>
          <div class="flex items-center justify-between mt-1">
            <div class="flex items-center gap-2 rounded-full border border-black/10 px-2 py-0.5 ${locked ? "opacity-50" : ""}">
              <button type="button" ${key ? `data-cart-step="-1" data-key="${key}"` : ""} ${locked ? "disabled" : ""} class="${qtyButton}" aria-label="Decrease ${escapeHtml(name)} quantity">−</button>
              <span class="min-w-[1.25rem] text-center text-sm font-semibold text-[#2d2e2d]">${quantity}</span>
              <button type="button" ${key ? `data-cart-step="1" data-key="${key}"` : ""} ${locked || increaseDisabled ? "disabled" : ""} class="${qtyButton}" aria-label="Increase ${escapeHtml(name)} quantity">+</button>
            </div>
            <div class="text-right">${price}</div>
          </div>
        </div>
      </div>`;
  }

  function render() {
    renderCount();
    if (!drawer || !cart) return;

    const lines = cart.items.filter((line) => !isProtectionLine(line) && !removing.has(line.key));
    const protectionLine = cart.items.find(isProtectionLine);
    const empty = lines.length === 0;
    const protectionOn = protectionPending ?? Boolean(protectionLine);

    $("[data-cart-empty]").hidden = !empty;
    $("[data-cart-lines]").hidden = empty;
    $("[data-cart-footer]").hidden = empty;
    $("[data-cart-shipping]").hidden = empty;

    let savings = 0;
    let qualifiesFree = false;
    const threshold = LV.localCents((LV.freeShippingThreshold || 9000) / 100);

    const html = lines
      .map((line) => {
        const bundle = bundleFor(line);
        const pending = stepping.has(line.key);
        // One-time lines are one bag per unit: the quantity is the bag count.
        const cans = pending ? stepping.get(line.key) : bundle && !bundle.once ? bundle.cans : line.quantity;
        const unitTotal = line.final_line_price ?? line.line_price;
        savings += lineSavings(line, bundle);
        if (bundle?.freeShipping || (line.final_price ?? line.price) >= threshold) qualifiesFree = true;

        const main = cartLine({
          key: line.key,
          image: bundle ? LV.gifts.blendImage : line.image,
          name: bundle ? "Lavendr Blend" : line.product_title,
          subtitle: bundle
            ? `${cans * SERVINGS_PER_CAN} Day Supply`
            : line.variant_title || "",
          quantity: cans,
          locked: false,
          increaseDisabled: !bundle || cans >= 3,
          removable: true,
          price: `<span class="text-sm font-semibold text-[#2d2e2d] transition-opacity ${pending ? "opacity-40" : ""}">${LV.money(unitTotal, cart.currency)}</span>`,
        });
        const gifts = giftsFor(bundle)
          .map((gift) =>
            cartLine({
              image: gift.image,
              name: gift.name,
              subtitle: gift.subtitle || "",
              quantity: 1,
              locked: true,
              price: `<div><p class="text-xs text-black/30 line-through leading-none mb-0.5">${LV.money(LV.localCents(gift.value), cart.currency)}</p><p class="text-sm font-semibold text-accent-1">FREE</p></div>`,
            }),
          )
          .join("");
        return `<div class="space-y-3">${main}${gifts}</div>`;
      })
      .join("");
    $("[data-cart-lines]").innerHTML = html;

    // Free shipping progress. The subtotal already counts optimistic removals
    // and the toggle's new position; the real figure lands with the response.
    let subtotal = cart.total_price;
    for (const line of cart.items) {
      if (removing.has(line.key)) subtotal -= line.final_line_price ?? line.line_price;
    }
    if (protectionOn && !protectionLine) subtotal += protectionVariant?.price ?? 0;
    if (!protectionOn && protectionLine) subtotal -= protectionLine.final_line_price ?? protectionLine.line_price;
    subtotal = Math.max(0, subtotal);
    const settling = stepping.size > 0;
    const remaining = Math.max(0, threshold - subtotal);
    const unlocked = qualifiesFree || remaining === 0;
    const progress = unlocked ? 100 : Math.min(100, (subtotal / threshold) * 100);
    const unlockedEl = $("[data-cart-shipping-unlocked]");
    const lockedEl = $("[data-cart-shipping-locked]");
    unlockedEl.setAttribute("aria-hidden", String(!unlocked));
    lockedEl.setAttribute("aria-hidden", String(unlocked));
    unlockedEl.classList.toggle("translate-y-0", unlocked);
    unlockedEl.classList.toggle("opacity-100", unlocked);
    unlockedEl.classList.toggle("pointer-events-none", !unlocked);
    unlockedEl.classList.toggle("translate-y-1", !unlocked);
    unlockedEl.classList.toggle("opacity-0", !unlocked);
    lockedEl.classList.toggle("pointer-events-none", unlocked);
    lockedEl.classList.toggle("-translate-y-1", unlocked);
    lockedEl.classList.toggle("opacity-0", unlocked);
    lockedEl.classList.toggle("translate-y-0", !unlocked);
    lockedEl.classList.toggle("opacity-100", !unlocked);
    $("[data-cart-shipping-remaining]").textContent = LV.money(remaining, cart.currency);
    $("[data-cart-shipping-progress]").style.width = `${progress}%`;

    // Totals.
    $("[data-cart-savings-row]").hidden = savings <= 0;
    $("[data-cart-savings]").textContent = `−${LV.money(savings, cart.currency)}`;
    $("[data-cart-subtotal]").textContent = LV.money(subtotal, cart.currency);
    $("[data-cart-subtotal]").classList.toggle("opacity-40", settling);

    // Protection toggle mirrors the cart line (or the tap, until it lands).
    const on = protectionOn;
    const toggle = $("[data-cart-protection]");
    toggle.hidden = !protectionVariant;
    toggle.setAttribute("aria-checked", String(on));
    $("[data-cart-protection-track]").classList.toggle("bg-accent-1", on);
    $("[data-cart-protection-track]").classList.toggle("bg-black/15", !on);
    $("[data-cart-protection-knob]").classList.toggle("translate-x-[1.125rem]", on);
    $("[data-cart-protection-knob]").classList.toggle("translate-x-0.5", !on);
    $("[data-cart-protection-price]").textContent = LV.money(
      protectionLine?.final_price ?? protectionVariant?.price ?? 300,
      cart.currency,
    );
  }

  function renderCount() {
    const count = cart
      ? cart.items.filter((line) => !isProtectionLine(line)).reduce((sum, line) => sum + line.quantity, 0)
      : 0;
    document.querySelectorAll("[data-cart-count]").forEach((badge) => {
      badge.hidden = count === 0;
      badge.textContent = count > 9 ? "9+" : String(count);
    });
  }

  /**
   * Removes a line in one request (update.js answers with the whole cart).
   * Removing the last product takes the protection line with it.
   */
  async function removeLine(key) {
    const updates = { [key]: 0 };
    const others = cart.items.filter((line) => line.key !== key && !isProtectionLine(line));
    if (!others.length) cart.items.filter(isProtectionLine).forEach((line) => (updates[line.key] = 0));
    setCart(await api.update({ updates }));
    await syncProtection();
  }

  /**
   * −/+ on a bundle swaps it for the 1/2/3-can bundle (with its plan).
   * One-time lines (one bag per unit) just change quantity, up to 3 bags.
   */
  async function step(key, delta) {
    const line = cart.items.find((item) => item.key === key);
    if (!line) return;
    const bundle = bundleFor(line);
    if (!bundle || bundle.once) {
      const quantity = Math.max(0, line.quantity + delta);
      if (bundle && quantity > 3) return;
      if (quantity === 0) return removeLine(key);
      // change.js answers with the whole cart: no reload needed.
      setCart(await api.change(key, quantity));
      await syncProtection();
      return;
    }
    const nextCans = bundle.cans + delta;
    if (nextCans <= 0) return removeLine(key);
    if (nextCans > 3) return;
    const [nextId, next] =
      Object.entries(LV.catalog).find(([, entry]) => entry.cans === nextCans && !entry.once) || [];
    if (!nextId) return;
    // Add the new bundle first, then drop the old one: the drop's response
    // is the finished cart, so the swap takes two requests instead of three.
    await api.add([
      {
        id: Number(nextId),
        quantity: 1,
        ...(line.selling_plan_allocation && next.sellingPlanId ? { selling_plan: next.sellingPlanId } : {}),
      },
    ]);
    setCart(await api.change(key, 0));
    LV.track("cart_upgrade", { fromVariantId: line.variant_id, toVariantId: Number(nextId) });
    await syncProtection();
  }

  async function checkout() {
    const button = $("[data-cart-checkout]");
    if (!cart?.items.length || button.disabled) return;
    button.disabled = true;
    button.textContent = "Redirecting to Checkout…";
    try {
      // Let a toggle or quantity change finish so checkout gets that cart.
      while (busy) await task;
      await lock(async () => {
        await syncProtection();
        await syncAttributes();
      });
      await LV.events.initiateCheckout(cart);
    } finally {
      window.location.href = "/checkout";
    }
  }

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (target.closest("[data-cart-toggle]")) {
      event.preventDefault();
      setOpen(!isOpen);
    } else if (target.closest("[data-cart-open]")) {
      event.preventDefault();
      open();
    } else if (target.closest("[data-cart-close]") || target === backdrop) {
      close();
    } else if (target.closest("[data-cart-remove]")) {
      const key = target.closest("[data-cart-remove]").getAttribute("data-cart-remove");
      if (busy || !cart) return;
      guarded(() => removeLine(key));
      removing.add(key); // hide it now; the request confirms
      render();
    } else if (target.closest("[data-cart-step]")) {
      const button = target.closest("[data-cart-step]");
      if (busy || !cart) return;
      const key = button.dataset.key;
      const delta = Number(button.dataset.cartStep);
      guarded(() => step(key, delta));
      // Show the new count now; at zero the line goes, as removeLine will do.
      const line = cart.items.find((item) => item.key === key);
      if (line) {
        const b = bundleFor(line);
        const shown = (b && !b.once ? b.cans : line.quantity) + delta;
        if (shown <= 0) removing.add(key);
        else if (shown <= 3 || !bundleFor(line)) stepping.set(key, shown);
        render();
      }
    } else if (target.closest("[data-cart-protection]")) {
      const next = !(protectionPending ?? (cart?.items || []).some(isProtectionLine));
      setProtectionWanted(next);
      protectionPending = next; // flip the toggle now; the request follows
      render();
      syncProtectionSoon();
    } else if (target.closest("[data-cart-checkout]")) {
      checkout();
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && isOpen) close();
  });

  // Back/forward cache: re-enable the checkout button and re-read the cart.
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    const button = $("[data-cart-checkout]");
    if (button) {
      button.disabled = false;
      button.innerHTML = 'Secure Checkout <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="size-5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg>';
    }
    refresh().catch(() => {});
  });

  // Back from another tab: re-read the cart, which may have changed there
  // (add-to-cart skips its clear when this copy says the cart is empty).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible" || busy) return;
    loading = refresh().catch(() => {});
  });

  loading = refresh().catch(() => {});
  loading
    .then(() => (cart ? syncAttributes() : null))
    .then(() => {
      // /cart redirects to the product page with ?cart=open (sections/main-cart.liquid).
      const params = new URLSearchParams(location.search);
      if (params.get("cart") === "open") {
        open();
        params.delete("cart");
        const query = params.toString();
        history.replaceState(null, "", location.pathname + (query ? `?${query}` : "") + location.hash);
      }
    })
    .catch(() => {});
})();

/* ── 20-popup.js ── */
/*
 * Email popup. Ported from src/components/EmailPopup.tsx.
 * The answer and experiment id become Shopify customer tags
 * (popup-goal:<id>, popup-experiment:<id>), which Omnisend syncs.
 * Up to three questions are rendered ([data-popup-question]); each visitor
 * gets one, split evenly and kept in localStorage so repeat visits match.
 *
 * A/B variant (off by default): the "pour the glass" minigame replaces the
 * question. Hold to pour Lavendr into the glass (the real product photo),
 * the saving climbs as it fills, let go at the line to unlock the top tier,
 * keep holding and it spills. It runs when <html> has the class
 * lv-ab-pour-game (added by the Intelligems test's injected JS, like
 * lv-ab-toggle), or with ?popup=game for testing. Its signups are tagged
 * popup-game:won, popup-tier:<amount>, popup-experiment:pour-game.
 */
(function () {
  const LV = window.Lavendr;
  const root = document.querySelector("[data-popup]");
  const config = LV.popup || {};

  const STORAGE_KEY = "lavendr_popup_dismissed";
  const QUESTION_KEY = "lavendr_popup_question";
  const MIN_LOAD_MS = 1000;
  const CLAIMED_VISIBLE_MS = 1000;
  const THREE_HOURS_MS = 3 * 60 * 60 * 1000;
  const EXPERIMENT = "pour-game";
  const GAME_CLASS = "lv-ab-pour-game";

  // Pages without the popup still honour CTAs by doing nothing, as before.
  if (!root || !config.enabled) return;

  const params = new URLSearchParams(location.search);
  const forceGame = params.get("popup") === "game";
  const gameOn = () => forceGame || document.documentElement.classList.contains(GAME_CLASS);

  const quiz = root.querySelector('[data-popup-step="quiz"]');

  const questions = Array.from(root.querySelectorAll("[data-popup-question]"));
  if (questions.length) {
    const ids = questions.map((q) => q.getAttribute("data-popup-question"));
    let chosen = null;
    try {
      chosen = localStorage.getItem(QUESTION_KEY);
    } catch {}
    if (!ids.includes(chosen)) {
      chosen = ids[Math.floor(Math.random() * ids.length)];
      try {
        localStorage.setItem(QUESTION_KEY, chosen);
      } catch {}
    }
    questions.forEach((q) => {
      q.hidden = q.getAttribute("data-popup-question") !== chosen;
    });
    config.experimentId = chosen;
  }
  const gameStep = root.querySelector('[data-popup-step="game"]');
  const emailStep = root.querySelector('[data-popup-step="email"]');
  const form = root.querySelector("[data-popup-form]");
  const input = form.querySelector('input[type="email"]');
  const submit = root.querySelector("[data-popup-submit]");
  const error = root.querySelector("[data-popup-error]");
  const labels = {
    idle: root.querySelector("[data-popup-label-idle]"),
    loading: root.querySelector("[data-popup-label-loading]"),
    claimed: root.querySelector("[data-popup-label-claimed]"),
  };

  let variant = "quiz"; // "quiz" | "game", decided each time the popup opens
  let selectedGoal = null;
  let deadline = null;
  let countdown = null;
  let timeouts = [];
  let dismissed = false;
  let exiting = false;
  let wonTier = null; // in the shopper's currency, for display
  let wonLadder = null; // the same tier in store dollars, for tags

  const later = (fn, ms) => timeouts.push(setTimeout(fn, ms));
  const clearLater = () => {
    timeouts.forEach(clearTimeout);
    timeouts = [];
  };

  // Amounts are whole units of the shopper's currency ("$185", "€171").
  const money = (units) => {
    try {
      if (LV.money) return LV.money(Math.round(units) * 100);
    } catch {}
    return `$${Math.round(units)}`;
  };

  // The top tier is the site's own "save up to" amount (snippets/max-savings,
  // converted by 00-core.js in other markets), so it matches the header.
  const maxSavingsUnits = () => {
    let cents = LV.maxSavingsCents;
    if (!(cents > 0) && LV.maxSavings) {
      const gifts = LV.maxSavings.giftStoreCents || 0;
      cents = (LV.maxSavings.discountCents || 0) + (LV.localCents ? LV.localCents(gifts / 100) : gifts);
    }
    return cents > 0 ? Math.round(cents / 100) : null;
  };

  /* ── Pour game ──────────────────────────────────────────────── */

  const game = (function () {
    const el = gameStep.querySelector("[data-pour]");
    const q = (sel) => el.querySelector(sel);
    const svgNS = "http://www.w3.org/2000/svg";

    // data-tiers is the ladder in store dollars ($30 ... $185). The top step
    // becomes the real max saving in the shopper's currency and the others
    // keep their share of it, rounded to a tidy step ($5, €5, ¥500 ...).
    const ladder = el.getAttribute("data-tiers").split(",").map(Number);
    let tiers = ladder.slice();
    function buildTiers() {
      const last = ladder[ladder.length - 1];
      const top = maxSavingsUnits() || last;
      const step = Math.max(1, 0.5 * 10 ** (Math.floor(Math.log10(top)) - 1));
      tiers = ladder.map((v, i) =>
        i === ladder.length - 1 ? top : Math.max(step, Math.round((top * v) / last / step) * step),
      );
    }
    const thresholds = el.getAttribute("data-thresholds").split(",").map(Number);
    const WIN_AT = thresholds[thresholds.length - 1];
    const SPILL_AT = 1.2;

    // Glass geometry in the photo's pixel space (productShots-mug.webp,
    // 1024x1024). Inner wall [y, left x, right x], top rim to the base.
    const WALL = [
      [222, 352, 648],
      [300, 350, 651],
      [332, 354, 648],
      [400, 342, 662],
      [500, 327, 672],
      [600, 320, 680],
      [700, 322, 677],
      [760, 340, 660],
      [790, 380, 630],
      [808, 430, 580],
    ];
    const Y_TOP = 222; // inner rim
    const Y_LIQUID = 328; // the photo's own liquid starts here
    const Y_EMPTY = 808; // below the liquid in the photo
    const Y_BOTTOM = 800; // fill 0
    const Y_FULL = 334; // fill 1: where the photo's own liquid stops
    const SPOUT = { x: 498, y: 110 };
    const FALL = 2000; // stream speed, photo px per second

    const emptyPath = q("[data-pour-empty-path]");
    const overPath = q("[data-pour-over-path]");
    const meniscus = q("[data-pour-meniscus]");
    const stream = q("[data-pour-stream]");
    const drops = q("[data-pour-drops]");
    const marks = q("[data-pour-marks]");
    const drip = { left: q('[data-pour-drip="left"]'), right: q('[data-pour-drip="right"]') };
    const puddle = q("[data-pour-puddle]");
    const amountEl = q("[data-pour-amount]");
    const maxBadge = q("[data-pour-max-badge]");
    const status = q("[data-pour-status]");
    const hold = q("[data-pour-hold]");
    const holdLabel = q("[data-pour-hold-label]");
    const claim = q("[data-pour-claim]");
    const win = q("[data-pour-win]");
    const stage = q("[data-pour-stage]");

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    let fill = 0; // 0 = empty, 1 = the photo's level, SPILL_AT = brim
    let state = "ready"; // ready | pouring | settling | spilling | won
    let holding = false;
    let pourTime = 0; // seconds since the stream reached the surface
    let head = null; // y of the stream's leading end while it falls
    let tail = null; // y of the stream's trailing end after letting go
    let slosh = 0; // surface wave energy
    let spills = 0;
    let spillT = 0;
    let shown = 0; // displayed saving, eases toward the tier
    let tierIndex = -1;
    let last = 0;
    let raf = 0;
    let t = 0;
    let dropList = [];
    let started = false;

    const lerp = (a, b, k) => a + (b - a) * k;
    const wallAt = (y) => {
      if (y <= WALL[0][0]) return [WALL[0][1], WALL[0][2]];
      for (let i = 1; i < WALL.length; i++) {
        const [y1, l1, r1] = WALL[i];
        if (y <= y1) {
          const [y0, l0, r0] = WALL[i - 1];
          const k = (y - y0) / (y1 - y0);
          return [lerp(l0, l1, k), lerp(r0, r1, k)];
        }
      }
      const end = WALL[WALL.length - 1];
      return [end[1], end[2]];
    };
    const levelY = (f) => Y_BOTTOM - f * (Y_BOTTOM - Y_FULL);
    const f2 = (n) => n.toFixed(1);

    // Surface line from left wall to right wall, wobbling with slosh.
    function surface(y) {
      const [l, r] = wallAt(y);
      const pts = [];
      const n = 18;
      const amp = Math.min(9, 1.2 + slosh * 7);
      for (let i = 0; i <= n; i++) {
        const x = lerp(l, r, i / n);
        const edge = Math.sin((Math.PI * i) / n); // calmer at the walls
        let dy = amp * edge * (Math.sin(x * 0.045 + t * 7.5) * 0.6 + Math.sin(x * 0.11 - t * 11) * 0.4);
        // the stream dimples the surface where it lands
        if (head === null && (holding || tail !== null)) dy += 6 * Math.exp(-((x - SPOUT.x) ** 2) / 500);
        pts.push([x, y + dy]);
      }
      return pts;
    }

    // Wall points between two heights, top to bottom, on one side.
    function wallPoints(side, yFrom, yTo) {
      const pts = [];
      const idx = side === "left" ? 1 : 2;
      WALL.forEach((w) => {
        if (w[0] > yFrom && w[0] < yTo) pts.push([w[idx], w[0]]);
      });
      return pts;
    }

    const toPath = (pts) => "M" + pts.map((p) => `${f2(p[0])} ${f2(p[1])}`).join("L") + "Z";

    function drawGlass() {
      const y = fill > 0.002 ? levelY(fill) : Y_EMPTY;
      const top = surface(Math.max(Y_TOP + 4, y));
      const yL = top[0][1];
      const yR = top[top.length - 1][1];
      // Empty glass: from the photo's liquid line down to the surface.
      if (y > Y_LIQUID + 2) {
        const [tl, tr] = wallAt(Y_LIQUID - 6);
        const empty = [[tl, Y_LIQUID - 6], [tr, Y_LIQUID - 6], ...wallPoints("right", Y_LIQUID - 6, yR), ...top.slice().reverse()];
        empty.push(...wallPoints("left", Y_LIQUID - 6, yL).reverse());
        emptyPath.setAttribute("d", toPath(empty));
      } else {
        emptyPath.setAttribute("d", "");
      }

      // Above the photo's own liquid we paint the overflow ourselves.
      if (y < Y_FULL + 6) {
        const yBase = Y_FULL + 10;
        const [bl, br] = wallAt(yBase);
        const over = [...top, ...wallPoints("right", yR, yBase), [br, yBase], [bl, yBase]];
        over.push(...wallPoints("left", yL, yBase).reverse());
        overPath.setAttribute("d", toPath(over));
      } else {
        overPath.setAttribute("d", "");
      }

      meniscus.setAttribute(
        "d",
        fill > 0.02 ? "M" + top.map((p) => `${f2(p[0])} ${f2(p[1])}`).join("L") : "",
      );
    }

    function drawStream() {
      const surf = fill > 0.002 ? levelY(fill) : Y_BOTTOM + 20;
      if (!holding && tail === null && head === null) {
        stream.setAttribute("d", "");
        return;
      }
      const yTop = tail === null ? SPOUT.y : tail;
      const yBot = head === null ? surf : Math.min(head, surf);
      if (yBot <= yTop) {
        stream.setAttribute("d", "");
        return;
      }
      // Narrows as it falls (it speeds up), wobbles a little.
      const segs = 14;
      const left = [];
      const right = [];
      for (let i = 0; i <= segs; i++) {
        const y = lerp(yTop, yBot, i / segs);
        const k = (y - SPOUT.y) / (Y_BOTTOM - SPOUT.y);
        const w = lerp(18, 11, Math.min(1, Math.max(0, k)));
        const x = SPOUT.x + Math.sin(y * 0.04 + t * 14) * 1.3 * k;
        left.push([x - w / 2, y]);
        right.push([x + w / 2, y]);
      }
      const capY = yBot + 4;
      stream.setAttribute(
        "d",
        "M" +
          left.map((p) => `${f2(p[0])} ${f2(p[1])}`).join("L") +
          `Q${SPOUT.x} ${f2(capY)} ` +
          right
            .reverse()
            .map((p) => `${f2(p[0])} ${f2(p[1])}`)
            .join("L") +
          "Z",
      );
    }

    function spawnDrops(n) {
      if (reduced) return;
      const y = levelY(fill);
      for (let i = 0; i < n; i++) {
        const c = document.createElementNS(svgNS, "circle");
        c.setAttribute("class", "lv-pour__drop");
        drops.appendChild(c);
        dropList.push({
          el: c,
          x: SPOUT.x + (Math.random() - 0.5) * 10,
          y: y - 2,
          vx: (Math.random() - 0.5) * 160,
          vy: -120 - Math.random() * 160,
          r: 2 + Math.random() * 2.6,
          life: 0,
        });
      }
    }

    function stepDrops(dt) {
      dropList = dropList.filter((d) => {
        d.life += dt;
        d.vy += 1100 * dt;
        d.x += d.vx * dt;
        d.y += d.vy * dt;
        if (d.life > 0.6 || d.y > levelY(fill) + 4) {
          d.el.remove();
          return false;
        }
        d.el.setAttribute("cx", f2(d.x));
        d.el.setAttribute("cy", f2(d.y));
        d.el.setAttribute("r", f2(d.r));
        return true;
      });
    }

    function buildMarks() {
      marks.textContent = "";
      thresholds.forEach((f, i) => {
        const y = levelY(f);
        const [, r] = wallAt(y);
        const isMax = i === thresholds.length - 1;
        const g = document.createElementNS(svgNS, "g");
        g.setAttribute("class", "lv-pour__mark" + (isMax ? " lv-pour__mark--max" : ""));
        // Dashed line across the glass at every tier.
        const [l] = wallAt(y);
        const line = document.createElementNS(svgNS, "line");
        line.setAttribute("x1", f2(l + 6));
        line.setAttribute("x2", f2(r + 16));
        line.setAttribute("y1", f2(y));
        line.setAttribute("y2", f2(y));
        line.setAttribute("class", "lv-pour__fill-line");
        g.appendChild(line);
        const text = document.createElementNS(svgNS, "text");
        text.setAttribute("x", f2(r + 28));
        text.setAttribute("y", f2(y + 9));
        text.textContent = money(tiers[i]);
        g.appendChild(text);
        marks.appendChild(g);
      });
    }

    function currentTier() {
      let idx = -1;
      thresholds.forEach((th, i) => {
        if (fill >= th) idx = i;
      });
      return idx;
    }

    function updateTier() {
      const idx = state === "spilling" ? -1 : currentTier();
      if (idx !== tierIndex) {
        const up = idx > tierIndex;
        tierIndex = idx;
        Array.from(marks.children).forEach((m, i) => m.classList.toggle("is-on", i <= idx));
        const isMax = idx === tiers.length - 1;
        maxBadge.hidden = !isMax;
        el.classList.toggle("is-max", isMax);
        if (up && idx >= 0) {
          amountEl.classList.remove("is-pop");
          void amountEl.offsetWidth;
          amountEl.classList.add("is-pop");
          navigator.vibrate?.(isMax ? [18, 40, 18] : 10);
        }
        if (isMax && holding) setStatus("MAX! Let go now!", "hot");
      }
      const target = idx >= 0 ? tiers[idx] : 0;
      shown = Math.abs(target - shown) < 0.5 ? target : lerp(shown, target, 0.2);
      amountEl.textContent = money(Math.round(shown));
    }

    function setStatus(text, tone) {
      status.textContent = text;
      status.dataset.tone = tone || "";
    }

    function frame(now) {
      const dt = Math.min(0.05, (now - last) / 1000 || 0.016);
      last = now;
      t += dt;
      const surf = levelY(fill);

      if (state === "pouring" || state === "settling") {
        // Stream head falling toward the surface.
        if (head !== null) {
          head += FALL * dt;
          if (head >= surf) {
            head = null;
            spawnDrops(5);
          }
        }
        const landing = head === null && (holding || tail !== null);
        if (landing) {
          pourTime += dt;
          const ramp = Math.min(1, pourTime / 1.5);
          const ease = Math.max(0.6, 0.86 ** spills);
          const rate = (0.15 + 0.3 * ramp) * ease;
          fill += rate * dt;
          slosh = Math.min(1, slosh + dt * 2.4);
          if (Math.random() < dt * 14) spawnDrops(1);
        } else {
          slosh = Math.max(0, slosh - dt * 1.4);
        }
        // After letting go the stream's tail falls in, then we judge.
        if (tail !== null) {
          tail += FALL * dt;
          if (tail >= surf) {
            tail = null;
            if (state === "settling") judge();
          }
        }
        if (fill >= SPILL_AT) startSpill();
      } else {
        slosh = Math.max(0, slosh - dt * 1.2);
      }

      if (state === "spilling") stepSpill(dt);

      drawGlass();
      drawStream();
      stepDrops(dt);
      updateTier();

      const idle = state === "ready" || state === "won";
      if (idle && slosh <= 0 && dropList.length === 0) {
        raf = 0;
        return;
      }
      raf = requestAnimationFrame(frame);
    }

    function loop() {
      if (raf) return;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }

    function press(event) {
      if (state !== "ready" && state !== "settling") return;
      if (event) event.preventDefault();
      if (holding) return;
      holding = true;
      if (!started) {
        started = true;
        LV.track?.("popup_game_start", { popupSelectionExperiment: EXPERIMENT });
      }
      if (state === "ready") {
        head = SPOUT.y;
        pourTime = 0;
      }
      tail = null;
      state = "pouring";
      el.classList.add("is-pouring");
      holdLabel.textContent = "Pouring…";
      setStatus(fill >= WIN_AT ? "MAX! Let go now!" : "Keep holding…", fill >= WIN_AT ? "hot" : "");
      loop();
    }

    function release() {
      if (!holding) return;
      holding = false;
      el.classList.remove("is-pouring");
      holdLabel.textContent = "Hold to Pour";
      if (state !== "pouring") return;
      state = "settling";
      if (head !== null) {
        // Let go before the stream even landed.
        head = null;
        tail = null;
        state = "ready";
        setStatus("Hold to pour. Let go at the line!");
        return;
      }
      tail = SPOUT.y;
    }

    function judge() {
      if (state !== "settling") return;
      if (fill >= WIN_AT && fill < SPILL_AT) {
        winGame();
      } else {
        state = "ready";
        const next = thresholds.findIndex((th) => fill < th);
        setStatus(
          fill < 0.5
            ? `Keep pouring! ${money(tiers[tiers.length - 1])} is at the line`
            : `So close! Pour a little more for ${money(tiers[Math.max(next, 0)])}`,
        );
      }
    }

    function startSpill() {
      if (state === "spilling") return;
      state = "spilling";
      holding = false;
      head = null;
      tail = null;
      spillT = 0;
      spills += 1;
      el.classList.remove("is-pouring", "is-max");
      el.classList.add("is-spilled");
      holdLabel.textContent = "Hold to Pour";
      hold.disabled = true;
      setStatus("Whoops, it spilled! Try again", "bad");
      navigator.vibrate?.(60);
      LV.track?.("popup_game_spill", { popupSelectionExperiment: EXPERIMENT, attempt: spills });
    }

    function dripPath(side, len) {
      const [l, r] = wallAt(Y_TOP);
      const outer = side === "left" ? l - 16 : r + 16;
      const dir = side === "left" ? -1 : 1;
      const yEnd = Y_TOP + len;
      const bulge = (y) => {
        // follow the glass outline, which widens toward the bottom
        const [wl, wr] = wallAt(Math.min(y, 800));
        return side === "left" ? wl - 14 : wr + 14;
      };
      const pts = [];
      for (let y = Y_TOP - 4; y <= yEnd; y += 24) pts.push([bulge(y), y]);
      const xEnd = bulge(yEnd);
      return (
        `M${f2(outer - dir * 18)} ${Y_TOP - 6}` +
        pts.map((p) => `L${f2(p[0] + dir * 15)} ${f2(p[1])}`).join("") +
        `Q${f2(xEnd + dir * 4)} ${f2(yEnd + 22)} ${f2(xEnd - dir * 4)} ${f2(yEnd)}` +
        pts
          .slice()
          .reverse()
          .map((p) => `L${f2(p[0] - dir * 1)} ${f2(p[1])}`)
          .join("") +
        "Z"
      );
    }

    function stepSpill(dt) {
      spillT += dt;
      const run = Math.min(1, spillT / 0.9);
      const len = (Y_EMPTY - Y_TOP - 10) * (1 - (1 - run) ** 2);
      drip.left.setAttribute("d", dripPath("left", len * 0.92));
      drip.right.setAttribute("d", dripPath("right", len));
      const p = Math.max(0, (spillT - 0.5) / 0.8);
      puddle.setAttribute("rx", f2(Math.min(1, p) * 250));
      puddle.setAttribute("ry", f2(Math.min(1, p) * 30));
      slosh = 1;
      // Drain and reset once the spill has landed.
      if (spillT > 1.5) fill = Math.max(0, fill - dt * 2.2);
      if (spillT > 1.5 && fill <= 0) resetGlass(true);
    }

    function resetGlass(afterSpill) {
      fill = 0;
      state = "ready";
      holding = false;
      head = null;
      tail = null;
      pourTime = 0;
      slosh = 0;
      drip.left.setAttribute("d", "");
      drip.right.setAttribute("d", "");
      puddle.setAttribute("rx", "0");
      puddle.setAttribute("ry", "0");
      el.classList.remove("is-spilled", "is-pouring", "is-max", "is-won");
      hold.disabled = false;
      holdLabel.textContent = "Hold to Pour";
      if (afterSpill) setStatus("Try again: let go at the line!", "");
      else setStatus("Hold to pour. Let go at the line!");
    }

    function confetti() {
      const box = q("[data-pour-confetti]");
      box.textContent = "";
      if (reduced) return;
      const colors = ["#8d47ad", "#b98ad0", "#e5d4ef", "#5e2482", "#ffffff"];
      for (let i = 0; i < 26; i++) {
        const s = document.createElement("span");
        s.style.left = `${Math.random() * 100}%`;
        s.style.background = colors[i % colors.length];
        s.style.animationDelay = `${Math.random() * 0.5}s`;
        s.style.animationDuration = `${1.4 + Math.random() * 1.1}s`;
        s.style.setProperty("--drift", `${(Math.random() - 0.5) * 80}px`);
        s.style.setProperty("--spin", `${(Math.random() - 0.5) * 720}deg`);
        box.appendChild(s);
      }
    }

    function winGame() {
      state = "won";
      const tier = tiers[tiers.length - 1];
      wonTier = tier;
      wonLadder = ladder[ladder.length - 1];
      el.classList.add("is-won");
      hold.hidden = true;
      claim.hidden = false;
      q("[data-pour-win-amount]").textContent = money(tier);
      win.hidden = false;
      confetti();
      setStatus("Wow! That's a perfect pour! 🤩", "good");
      navigator.vibrate?.([20, 50, 20, 50, 40]);
      LV.track?.("popup_game_win", { popupSelectionExperiment: EXPERIMENT, attempts: spills + 1, tier });
    }

    function reset() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      dropList.forEach((d) => d.el.remove());
      dropList = [];
      spills = 0;
      started = false;
      shown = 0;
      tierIndex = -1;
      wonTier = null;
      wonLadder = null;
      buildTiers();
      win.hidden = true;
      hold.hidden = false;
      claim.hidden = true;
      buildMarks();
      resetGlass(false);
      maxBadge.hidden = true;
      amountEl.textContent = money(0);
      drawGlass();
      drawStream();
    }

    // Hold on the button or on the glass itself; Space/Enter work too.
    [hold, stage].forEach((target) => {
      target.addEventListener("pointerdown", (event) => {
        if (event.button > 0) return;
        try {
          target.setPointerCapture?.(event.pointerId);
        } catch {}
        press(event);
      });
      target.addEventListener("pointerup", release);
      target.addEventListener("pointercancel", release);
      target.addEventListener("lostpointercapture", release);
      target.addEventListener("contextmenu", (event) => event.preventDefault());
    });
    hold.addEventListener("keydown", (event) => {
      if ((event.key === " " || event.key === "Enter") && !event.repeat) press(event);
    });
    hold.addEventListener("keyup", (event) => {
      if (event.key === " " || event.key === "Enter") release();
    });
    hold.addEventListener("blur", release);

    return { reset, claim };
  })();

  /* ── Popup shell ────────────────────────────────────────────── */

  const experimentId = () => (variant === "game" ? EXPERIMENT : config.experimentId);

  function setStatus(status) {
    labels.idle.hidden = status !== "idle";
    labels.loading.hidden = status !== "loading";
    labels.claimed.hidden = status !== "claimed";
    submit.disabled = status !== "idle";
    input.disabled = status !== "idle";
    submit.classList.toggle("bg-emerald-600", status === "claimed");
    submit.classList.toggle("shadow-emerald-600/25", status === "claimed");
    submit.classList.toggle("bg-accent-1", status !== "claimed");
    submit.classList.toggle("shadow-accent-1/25", status !== "claimed");
  }

  function reset() {
    clearLater();
    clearInterval(countdown);
    variant = gameOn() ? "game" : "quiz";
    dismissed = false;
    exiting = false;
    selectedGoal = null;
    deadline = null;
    quiz.hidden = variant !== "quiz";
    gameStep.hidden = variant !== "game";
    emailStep.hidden = true;
    // The email step's copy differs per variant.
    root.querySelectorAll("[data-popup-variant]").forEach((el) => {
      el.hidden = el.getAttribute("data-popup-variant") !== variant;
    });
    input.value = "";
    error.hidden = true;
    setStatus("idle");
    root.querySelectorAll("[data-popup-goal]").forEach((button) => {
      button.classList.remove("brightness-90", "ring-2", "ring-accent-1", "ring-offset-2", "ring-offset-white");
      button.classList.add("hover:brightness-110");
    });
    if (variant === "game") game.reset();
  }

  function show() {
    root.classList.remove("animate-[fadeOut_200ms_ease-out_forwards]");
    root.classList.add("animate-[fadeIn_300ms_ease-out]");
    root.hidden = false;
    LV.track("popup_selection_view", { popupSelectionExperiment: experimentId() });
  }

  function exit() {
    if (exiting) return;
    exiting = true;
    root.classList.remove("animate-[fadeIn_300ms_ease-out]");
    root.classList.add("animate-[fadeOut_200ms_ease-out_forwards]");
    const done = (event) => {
      if (event && event.target !== root) return;
      root.removeEventListener("animationend", done);
      root.hidden = true;
      clearInterval(countdown);
    };
    root.addEventListener("animationend", done);
    // Reduced motion / no animation support.
    setTimeout(() => done(), 260);
  }

  function dismiss() {
    if (exiting) return;
    dismissed = true;
    clearLater();
    LV.session?.setItem(STORAGE_KEY, "1");
    exit();
  }

  function renderCountdown() {
    const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    const pad = (n) => String(n).padStart(2, "0");
    root.querySelector("[data-popup-hh]").textContent = pad(Math.floor(seconds / 3600));
    root.querySelector("[data-popup-mm]").textContent = pad(Math.floor((seconds % 3600) / 60));
    root.querySelector("[data-popup-ss]").textContent = pad(seconds % 60);
  }

  function toEmailStep() {
    deadline = Date.now() + THREE_HOURS_MS;
    if (variant === "game") {
      root.querySelectorAll("[data-popup-won-amount]").forEach((el) => {
        el.textContent = money(wonTier || 0);
      });
    }
    quiz.hidden = true;
    gameStep.hidden = true;
    emailStep.hidden = false;
    renderCountdown();
    countdown = setInterval(renderCountdown, 1000);
  }

  function chooseGoal(button) {
    if (selectedGoal) return;
    selectedGoal = button.getAttribute("data-popup-goal");
    button.classList.remove("hover:brightness-110");
    button.classList.add("brightness-90", "ring-2", "ring-accent-1", "ring-offset-2", "ring-offset-white");
    LV.track("popup_selection_answer", {
      popupSelectionExperiment: config.experimentId,
      goal: selectedGoal,
    });
    later(toEmailStep, 300);
  }

  async function onSubmit(event) {
    event.preventDefault();
    const email = input.value.trim();
    if (!email) return;
    dismissed = false;
    error.hidden = true;
    setStatus("loading");
    const started = Date.now();
    const game = variant === "game";
    const goal = game ? (wonLadder ? `pour-${wonLadder}` : null) : selectedGoal;
    try {
      await LV.subscribe(email, {
        source: "email-popup",
        tags: (game
          ? ["popup-game:won", wonLadder && `popup-tier:${wonLadder}`, `popup-experiment:${EXPERIMENT}`]
          : [
              selectedGoal && `popup-goal:${selectedGoal}`,
              config.experimentId && `popup-experiment:${config.experimentId}`,
            ]
        ).filter(Boolean),
      });
      await new Promise((resolve) => later(resolve, Math.max(0, MIN_LOAD_MS - (Date.now() - started))));
      if (dismissed) return;
      LV.session?.setItem(STORAGE_KEY, "1");
      setStatus("claimed");
      LV.events.lead(email, "email_popup", {
        goal,
        popupSelectionExperiment: experimentId(),
      });
      later(() => {
        if (!dismissed) exit();
      }, CLAIMED_VISIBLE_MS);
    } catch (err) {
      if (dismissed) return;
      error.textContent = err?.message || "Something went wrong.";
      error.hidden = false;
      setStatus("idle");
    }
  }

  root.addEventListener("click", (event) => {
    if (event.target.closest("[data-popup-dismiss]")) dismiss();
    const goal = event.target.closest("[data-popup-goal]");
    if (goal) chooseGoal(goal);
    if (event.target.closest("[data-pour-claim]")) toEmailStep();
  });
  form.addEventListener("submit", onSubmit);

  function openFromCta() {
    reset();
    show();
  }
  window.addEventListener("lavendr-open-email-popup", openFromCta);
  document.addEventListener("click", (event) => {
    if (!event.target.closest("[data-open-popup]")) return;
    event.preventDefault();
    openFromCta();
  });
  LV.openPopup = openFromCta;

  // Testing: ?popup opens it right away (even after it was dismissed or
  // claimed this session); ?popup=game opens the pour game variant.
  if (params.has("popup")) {
    LV.session?.removeItem?.(STORAGE_KEY);
    reset();
    show();
    return;
  }

  // Quiz finishers already gave their answers; the timed popup skips them.
  const quizVisitor = () => Boolean(LV.session?.getItem("lavendr_quiz_visitor"));
  if (config.autoShow && !LV.session?.getItem(STORAGE_KEY) && !quizVisitor()) {
    setTimeout(() => {
      if (root.hidden && !LV.session?.getItem(STORAGE_KEY) && !quizVisitor()) {
        reset();
        show();
      }
    }, config.delayMs || 5000);
  }
})();

/* ── 30-header.js ── */
/* Header mobile menu + currency selector (Header.tsx, CurrencySelector.tsx). */
(function () {
  const panel = document.querySelector("[data-menu-panel]");
  const backdrop = document.querySelector("[data-menu-backdrop]");

  function setMenu(open) {
    if (!panel) return;
    panel.classList.toggle("translate-x-0", open);
    panel.classList.toggle("-translate-x-full", !open);
    backdrop?.classList.toggle("opacity-100", open);
    backdrop?.classList.toggle("opacity-0", !open);
    backdrop?.classList.toggle("pointer-events-none", !open);
    document.body.style.overflow = open ? "hidden" : "";
  }

  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-menu-open]")) setMenu(true);
    else if (event.target.closest("[data-menu-close]") || event.target === backdrop) setMenu(false);
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") setMenu(false);
  });

  /* Currency selector: switches the Shopify Market via the localization form. */
  document.querySelectorAll("[data-currency-selector]").forEach((root) => {
    const trigger = root.querySelector("[data-currency-trigger]");
    const menu = root.querySelector("[data-currency-menu]");
    const chevron = root.querySelector("[data-currency-chevron]");
    const options = [...root.querySelectorAll("[data-country]")];
    const form = root.querySelector("form");
    const input = root.querySelector("[data-currency-input]");
    if (!trigger || !menu || !form) return;

    const setOpen = (open) => {
      menu.hidden = !open;
      trigger.setAttribute("aria-expanded", String(open));
      chevron?.classList.toggle("rotate-180", open);
      if (!open) return;
      const selected = options.find((o) => o.getAttribute("aria-checked") === "true") || options[0];
      selected?.focus();
      selected?.scrollIntoView({ block: "nearest" });
    };

    trigger.addEventListener("click", () => setOpen(menu.hidden));
    trigger.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        setOpen(true);
      }
    });
    menu.addEventListener("keydown", (event) => {
      const index = options.indexOf(document.activeElement);
      let next = index;
      if (event.key === "ArrowDown") next = (index + 1) % options.length;
      else if (event.key === "ArrowUp") next = (index - 1 + options.length) % options.length;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = options.length - 1;
      else if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        trigger.focus();
        return;
      } else if (/^[a-z]$/i.test(event.key)) {
        // Type a letter to jump to the next country starting with it.
        const startsWith = (o) =>
          (o.getAttribute("data-country-name") || "").toLowerCase().startsWith(event.key.toLowerCase());
        const after = options.findIndex((o, i) => i > index && startsWith(o));
        next = after !== -1 ? after : options.findIndex(startsWith);
        if (next === -1) return;
      } else return;
      event.preventDefault();
      options[next]?.focus();
    });
    options.forEach((option) =>
      option.addEventListener("click", () => {
        setOpen(false);
        const country = option.getAttribute("data-country");
        if (option.getAttribute("aria-checked") === "true") return;
        input.value = country;
        form.submit();
      }),
    );
    document.addEventListener("pointerdown", (event) => {
      if (!root.contains(event.target)) setOpen(false);
    });
  });
})();

/* ── 40-newsletter.js ── */
/*
 * Footer (and any other) newsletter forms. Markup contract:
 *   [data-newsletter][data-newsletter-source="footer_newsletter"]
 *     form[data-newsletter-form] > input[type=email], button
 *       [data-newsletter-idle] / [data-newsletter-loading] inside the button
 *     [data-newsletter-success] (hidden), [data-newsletter-error] (hidden)
 *     [data-newsletter-body] elements hide on success
 * Subscribes through Shopify's newsletter form (LV.subscribe), which the
 * Omnisend app syncs into Omnisend.
 */
(function () {
  const LV = window.Lavendr;

  document.querySelectorAll("[data-newsletter]").forEach((root) => {
    const form = root.querySelector("[data-newsletter-form]");
    if (!form) return;
    const input = form.querySelector('input[type="email"]');
    const button = form.querySelector('button[type="submit"]');
    const idle = form.querySelector("[data-newsletter-idle]");
    const loading = form.querySelector("[data-newsletter-loading]");
    const error = root.querySelector("[data-newsletter-error]");
    const success = root.querySelector("[data-newsletter-success]");
    const source = root.getAttribute("data-newsletter-source") || "newsletter";

    const setLoading = (on) => {
      input.disabled = on;
      button.disabled = on;
      if (idle) idle.hidden = on;
      if (loading) loading.hidden = !on;
    };

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const email = input.value.trim();
      if (!email) return;
      setLoading(true);
      if (error) error.hidden = true;
      try {
        await LV.subscribe(email, { source });
        LV.events.lead(email, source);
        root.querySelectorAll("[data-newsletter-body]").forEach((el) => (el.hidden = true));
        if (error) error.hidden = true;
        if (success) success.hidden = false;
        input.value = "";
      } catch (err) {
        if (error) {
          error.textContent = err?.message || "Something went wrong. Please try again.";
          error.hidden = false;
        }
      } finally {
        setLoading(false);
      }
    });
  });
})();

/* ── 50-product.js ── */
/*
 * Product page purchase logic. Ported from LavendrBlendProduct.tsx (offer4:
 * forceSubscribe + directCheckout). Drives snippets/product-buy-box.liquid
 * (rendered twice) and snippets/product-sticky-bars.liquid.
 */
(function () {
  const LV = window.Lavendr;
  const dataEl = document.querySelector("script[data-product-bundles]");
  if (!dataEl) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }
  const bundles = Object.fromEntries(data.bundles.map((b) => [b.cans, b]));
  let selected = data.defaultCans || 2;
  let adding = false;

  /* ── Bundle selection ─────────────────────────────────────────── */

  function select(cans) {
    if (!bundles[cans]) return;
    selected = cans;
    document.querySelectorAll("[data-bundle-option]").forEach((option) => {
      const on = Number(option.getAttribute("data-bundle-option")) === cans;
      option.setAttribute("aria-checked", String(on));
      option.classList.toggle("border-accent-1", on);
      option.classList.toggle("bg-[#fdf4fd]", on);
      option.classList.toggle("border-black/8", !on);
      option.classList.toggle("bg-white", !on);
      option.classList.toggle("text-[#2d2e2d]/65", !on);
      option.querySelector("[data-bundle-option-price]")?.classList.toggle("text-accent-1", on);
    });
    // Panels toggle by bundle; re-showing restarts receipt-pop / savings-pop.
    document.querySelectorAll("[data-bundle-panel]").forEach((panel) => {
      panel.hidden = Number(panel.getAttribute("data-bundle-panel")) !== cans;
    });
  }

  /* ── Subscribe & Save switch ──────────────────────────────────────
     On (default): the bundle variant on its plan, with gifts.
     Off: the one-time product's variant at quantity = bags, no plan, no
     gifts. html[data-purchase] drives what shows (theme.css). */

  let subscribe = true;

  function setSubscribe(on) {
    subscribe = on;
    document.documentElement.setAttribute("data-purchase", on ? "sub" : "once");
    document.querySelectorAll("[data-plan-toggle]").forEach((toggle) => {
      toggle.setAttribute("aria-checked", String(on));
    });
  }
  // Without one-time variants there is no switch and nothing to change.
  const hasOnce = data.bundles.every((b) => b.once);
  if (hasOnce) setSubscribe(true);

  /* "Are You Sure?" before turning it off (snippets/subscribe-confirm.liquid).
     Keep, Esc and a backdrop click all leave Subscribe & Save on. */
  const confirmEl = document.querySelector("[data-subscribe-confirm]");
  let confirmReturnFocus = null;
  const onConfirmKey = (event) => {
    if (event.key === "Escape") closeConfirm(false);
  };
  function openConfirm(trigger) {
    if (!confirmEl) return setSubscribe(false);
    // On <body> so the backdrop also covers the sticky header and bars.
    if (confirmEl.parentElement !== document.body) document.body.appendChild(confirmEl);
    clearTimeout(confirmEl._closeTimer);
    confirmReturnFocus = trigger;
    confirmEl.hidden = false;
    void confirmEl.offsetWidth; // start the fade from opacity 0
    confirmEl.setAttribute("data-open", "");
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onConfirmKey);
    confirmEl.querySelector("[data-subscribe-confirm-keep]")?.focus({ preventScroll: true });
  }
  function closeConfirm(turnOff) {
    if (!confirmEl || !confirmEl.hasAttribute("data-open")) return;
    confirmEl.removeAttribute("data-open");
    confirmEl._closeTimer = setTimeout(() => {
      confirmEl.hidden = true;
    }, window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 300);
    document.body.style.overflow = "";
    window.removeEventListener("keydown", onConfirmKey);
    if (turnOff) setSubscribe(false);
    confirmReturnFocus?.focus({ preventScroll: true });
  }
  confirmEl?.addEventListener("click", (event) => {
    if (event.target.closest("[data-subscribe-confirm-off]")) closeConfirm(true);
    else if (event.target.closest("[data-subscribe-confirm-keep]") || !event.target.closest("[data-subscribe-confirm-panel]")) closeConfirm(false);
  });

  /* ── Add to cart (replace cart + open drawer, as before) ──────── */

  function setAdding(on) {
    adding = on;
    document.querySelectorAll("[data-add-bundle], [data-sticky-cta]").forEach((button) => {
      button.disabled = on;
    });
    document.querySelectorAll("[data-add-label]").forEach((el) => (el.hidden = on));
    document.querySelectorAll("[data-adding-label]").forEach((el) => (el.hidden = !on));
    document.querySelectorAll("[data-sticky-label]").forEach((el) => {
      el.hidden = on || Number(el.getAttribute("data-bundle-panel")) !== selected;
    });
  }

  async function add() {
    if (adding) return;
    const bundle = bundles[selected];
    const once = !subscribe && hasOnce ? bundle.once : null;
    setAdding(true);
    const started = Date.now();
    try {
      await LV.addBundle({
        variantId: once ? once.variantId : bundle.variantId,
        sellingPlanId: once ? null : bundle.sellingPlanId,
        quantity: once ? once.quantity : bundle.quantity || 1,
        name: once ? once.title : bundle.title,
        price: once ? once.price : bundle.price,
      });
    } finally {
      // The old guard kept the button busy for 800ms.
      setTimeout(() => setAdding(false), Math.max(0, 800 - (Date.now() - started)));
    }
  }

  document.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-plan-toggle]");
    if (toggle) {
      // Turning it back on needs no warning.
      if (subscribe) openConfirm(toggle);
      else setSubscribe(true);
      return;
    }
    const option = event.target.closest("[data-bundle-option]");
    if (option) {
      select(Number(option.getAttribute("data-bundle-option")));
      return;
    }
    if (event.target.closest("[data-add-bundle], [data-sticky-cta]")) add();
  });

  /* ── Sticky bars: show once the picker scrolls above the viewport ── */

  const state = { mobile: false, desktop: false, footer: false, cart: false };
  const mobileBar = document.querySelector("[data-sticky-mobile]");
  const desktopBar = document.querySelector("[data-sticky-desktop]");

  function renderSticky() {
    const showMobile = state.mobile && !state.footer && !state.cart;
    const showDesktop = state.desktop && !state.footer && !state.cart;
    mobileBar?.classList.toggle("translate-y-0", showMobile);
    mobileBar?.classList.toggle("translate-y-full", !showMobile);
    desktopBar?.classList.toggle("opacity-100", showDesktop);
    desktopBar?.classList.toggle("translate-y-0", showDesktop);
    desktopBar?.classList.toggle("opacity-0", !showDesktop);
    desktopBar?.classList.toggle("translate-y-4", !showDesktop);
    desktopBar?.classList.toggle("pointer-events-none", !showDesktop);
  }

  const watchPast = (el, key) => {
    if (!el) return;
    new IntersectionObserver(
      ([entry]) => {
        state[key] = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        renderSticky();
      },
      { threshold: 0 },
    ).observe(el);
  };
  watchPast(document.getElementById("buy-box-mobile"), "mobile");
  watchPast(document.getElementById("buy-box-desktop"), "desktop");

  const footer = document.querySelector("footer");
  if (footer) {
    new IntersectionObserver(
      ([entry]) => {
        state.footer = entry.isIntersecting;
        renderSticky();
      },
      { threshold: 0 },
    ).observe(footer);
  }
  LV.on("lavendr:cart-open", (event) => {
    state.cart = Boolean(event.detail);
    renderSticky();
  });

  /* ── "Exclusive Gifts on Orders by Oct 17th" ──────────────────── */

  const now = new Date();
  const day = now.getDate();
  const ordinal =
    day % 10 === 1 && day % 100 !== 11
      ? "st"
      : day % 10 === 2 && day % 100 !== 12
        ? "nd"
        : day % 10 === 3 && day % 100 !== 13
          ? "rd"
          : "th";
  const label = `${now.toLocaleDateString("en-US", { month: "short" })} ${day}${ordinal}`;
  document.querySelectorAll("[data-offer-date]").forEach((el) => (el.textContent = label));

  /* ── ViewContent (no-op hook; Shopify and Omnisend track product views) ── */

  const initial = bundles[selected];
  LV.events.viewContent({
    variantId: initial.variantId,
    name: initial.title,
    price: initial.price,
    compareAtPrice: initial.compareAt,
    image: data.image,
  });
})();

/* ── c-before-after.js ── */
/* Before and after carousel (sections/before-after.liquid): arrows, dots and mouse drag over a scroll-snap track. */
(function () {
  const reduce = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  document.querySelectorAll("[data-ba-carousel]").forEach((root) => {
    const track = root.querySelector("[data-ba-track]");
    const dots = Array.from(root.querySelectorAll("[data-ba-dot]"));
    if (!track) return;
    const slides = () => Array.from(track.children);
    let active = 0;
    let settleTimer = 0;

    // Where the track has to be scrolled for a slide to sit at its left edge.
    const centreOf = (slide) => slide.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || "0");

    function nearestIndex() {
      let best = 0;
      slides().forEach((slide, i) => {
        if (Math.abs(centreOf(slide) - track.scrollLeft) < Math.abs(centreOf(slides()[best]) - track.scrollLeft)) best = i;
      });
      return best;
    }

    function paintDots() {
      dots.forEach((dot, i) => dot.setAttribute("aria-current", i === active ? "true" : "false"));
    }

    function goTo(index) {
      const items = slides();
      const next = Math.max(0, Math.min(items.length - 1, index));
      track.scrollTo({ left: centreOf(items[next]), behavior: reduce() ? "instant" : "smooth" });
    }

    track.addEventListener(
      "scroll",
      () => {
        const index = nearestIndex();
        if (index !== active) {
          active = index;
          paintDots();
        }
      },
      { passive: true },
    );

    root.querySelector("[data-ba-prev]")?.addEventListener("click", () => goTo(active - 1));
    root.querySelector("[data-ba-next]")?.addEventListener("click", () => goTo(active + 1));
    dots.forEach((dot) => dot.addEventListener("click", () => goTo(Number(dot.getAttribute("data-ba-dot")))));
    track.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
      event.preventDefault();
      goTo(active + (event.key === "ArrowRight" ? 1 : -1));
    });

    // Mouse drag (touch and trackpads already scroll natively).
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;

    track.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = lastX = event.clientX;
      startScroll = track.scrollLeft;
      lastT = performance.now();
      velocity = 0;
      clearTimeout(settleTimer);
    });

    window.addEventListener("pointermove", (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) < 4) return;
      if (!moved) {
        moved = true;
        track.style.scrollSnapType = "none";
        track.style.cursor = "grabbing";
      }
      track.scrollLeft = startScroll - dx;
      const now = performance.now();
      if (now > lastT) {
        velocity = velocity * 0.6 + ((event.clientX - lastX) / (now - lastT)) * 0.4;
        lastX = event.clientX;
        lastT = now;
      }
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      if (!moved) return;
      track.style.cursor = "";
      let index = nearestIndex();
      // A flick carries on to the next photo even when the drag was short.
      if (Math.abs(velocity) > 0.4) {
        const ahead = velocity < 0 ? 1 : -1;
        const items = slides();
        const offCentre = centreOf(items[index]) - track.scrollLeft;
        if ((ahead === 1 && offCentre <= 0) || (ahead === -1 && offCentre >= 0)) index += ahead;
      }
      track.style.scrollSnapType = "none"; // keep snapping off while the smooth scroll runs
      goTo(index);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        track.style.scrollSnapType = "";
      }, reduce() ? 0 : 600);
    }
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);

    track.addEventListener(
      "click",
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }
      },
      true,
    );
    track.addEventListener("dragstart", (event) => event.preventDefault());

    // Scrolling the page slides the photos sideways, and the cards wiggle in a wave
    // that travels along the row as they move.
    let lastY = window.scrollY;
    function wiggle() {
      const items = slides();
      const w = items[0].getBoundingClientRect().width || 300;
      const t = window.scrollY * 0.004;
      items.forEach((card, i) => {
        const rect = card.getBoundingClientRect();
        const phase = ((rect.left + rect.width / 2) / (w * 1.2)) * 1.6 + t;
        card.style.transform = `translateY(${(Math.sin(phase) * 20).toFixed(1)}px) rotate(${(Math.sin(phase + 1.3) * 3.2).toFixed(2)}deg)`;
      });
    }
    function onPageScroll() {
      const dy = window.scrollY - lastY;
      lastY = window.scrollY;
      if (reduce()) return;
      const rect = root.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (dy && !dragging && rect.top < vh && rect.bottom > 0) {
        clearTimeout(settleTimer);
        track.scrollLeft += dy * 1.4;
      }
      wiggle();
    }
    if (!reduce()) {
      window.addEventListener("scroll", onPageScroll, { passive: true });
      window.addEventListener("resize", wiggle);
      track.addEventListener("scroll", wiggle, { passive: true });
      wiggle();
    }

    paintDots();
  });
})();

/* ── c-count-up-stat.js ── */
/* CountUpStat (snippets/count-up-stat.liquid): count [data-count-up] up from 0 once 25% is visible. */
(function () {
  const stats = document.querySelectorAll("[data-count-up]");
  if (!stats.length) return;

  function parse(value) {
    const prefix = value.startsWith("~") ? "~" : "";
    const rest = prefix ? value.slice(1) : value;
    const match = rest.match(/^([\d.]+)(.*)$/);
    if (!match) return null;
    const target = parseFloat(match[1]);
    if (!(target > 0)) return null;
    const decimals = match[1].includes(".") ? match[1].split(".")[1].length : 0;
    return { prefix, target, suffix: match[2], decimals };
  }

  const format = (stat, amount) =>
    `${stat.prefix}${stat.decimals > 0 ? amount.toFixed(stat.decimals) : Math.round(amount).toString()}${stat.suffix}`;

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  function run(el, stat) {
    const duration = 1000;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      el.textContent = format(stat, stat.target * easeOutCubic(progress));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  stats.forEach((el) => {
    const stat = parse(el.getAttribute("data-count-up") || "");
    if (!stat) return;
    if (typeof IntersectionObserver === "undefined") {
      el.textContent = format(stat, stat.target);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        run(el, stat);
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
  });
})();

/* ── c-expert-reviews.js ── */
/* ExpertReviews (snippets/expert-reviews.liquid): slider dots follow the centred card; a dot centres its card. */
(function () {
  const LV = (window.Lavendr = window.Lavendr || {});

  document.querySelectorAll("[data-expert-reviews]").forEach((root) => {
    const track = root.querySelector("[data-expert-reviews-track]");
    const dots = root.querySelector("[data-slider-dots]");
    if (!track) return;
    let active = 0;

    const goTo = (index) => {
      const card = track.children[index];
      if (!card) return;
      track.scrollTo({
        left: card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2,
        behavior: "smooth",
      });
    };

    track.addEventListener(
      "scroll",
      () => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let best = Infinity;
        Array.from(track.children).forEach((el, index) => {
          const distance = Math.abs(el.offsetLeft + el.offsetWidth / 2 - center);
          if (distance < best) {
            best = distance;
            closest = index;
          }
        });
        if (closest === active) return;
        active = closest;
        LV.sliderDots?.set(dots, closest);
      },
      { passive: true },
    );

    // LV.sliderDots comes from c-slider-dots.js, which loads after this file.
    dots?.addEventListener("click", (event) => {
      const index = LV.sliderDots ? LV.sliderDots.index(event) : -1;
      if (index >= 0) goTo(index);
    });
  });
})();

/* ── c-faq.js ── */
/* ProductFaq accordion (snippets/faq-item.liquid). */
(function () {
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-faq-trigger]");
    if (!trigger) return;
    const item = trigger.closest("[data-faq-item]");
    const container = item.closest("[data-faq]");
    const setOpen = (row, open) => {
      row.querySelector("[data-faq-trigger]").setAttribute("aria-expanded", String(open));
      row.querySelector("[data-faq-chevron]")?.classList.toggle("rotate-180", open);
      const panel = row.querySelector("[data-faq-panel]");
      panel.classList.toggle("grid-rows-[1fr]", open);
      panel.classList.toggle("grid-rows-[0fr]", !open);
    };
    const open = trigger.getAttribute("aria-expanded") !== "true";
    if (open && container?.hasAttribute("data-faq-single")) {
      container.querySelectorAll("[data-faq-item]").forEach((row) => {
        if (row !== item) setOpen(row, false);
      });
    }
    setOpen(item, open);
  });
})();

/* ── c-hand-drawn-underline.js ── */
/* HandDrawnUnderline (snippets/hand-drawn-underline.liquid): draw the underline once 40% is visible. */
(function () {
  const items = document.querySelectorAll("[data-hand-drawn-underline]");
  if (!items.length) return;

  const draw = (el) => {
    const path = el.querySelector("path");
    if (!path) return;
    path.classList.remove("hand-drawn-underline-idle");
    path.classList.add("hand-drawn-underline-animate");
  };

  items.forEach((el) => {
    if (typeof IntersectionObserver === "undefined") return draw(el);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        draw(el);
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
  });
})();

/* ── c-ingredients-section.js ── */
/* IngredientsSection (snippets/ingredients-section.liquid): each card toggles its science panel. */
(function () {
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-ingredient-trigger]");
    if (!trigger) return;
    const card = trigger.closest("[data-ingredient]");
    const open = trigger.getAttribute("aria-expanded") !== "true";
    trigger.setAttribute("aria-expanded", String(open));
    card.querySelector("[data-ingredient-chevron]")?.classList.toggle("rotate-180", open);
    const panel = card.querySelector("[data-ingredient-panel]");
    panel?.classList.toggle("grid-rows-[1fr]", open);
    panel?.classList.toggle("grid-rows-[0fr]", !open);
  });
})();

/* ── c-ingredients-slider.js ── */
/*
 * IngredientsSlider (snippets/ingredients-slider.liquid): infinite 3-D
 * coverflow. Slots are keyed by a virtual index (like the React keys), so
 * cards that stay in range animate and new ones appear in place.
 */
(function () {
  const mod = (n, d) => ((n % d) + d) % d;
  const TRANSITION = "all 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)";

  document.querySelectorAll("[data-ingredients-slider]").forEach((root) => {
    const stage = root.querySelector("[data-ingredients-stage]");
    if (!stage) return;
    const LV = (window.Lavendr = window.Lavendr || {});
    const dots = root.querySelector("[data-slider-dots]");
    const length = dots?.querySelectorAll("[data-slider-dot]").length || 6;

    // One pristine card per ingredient to clone new slots from.
    const templates = [];
    stage.querySelectorAll("[data-vi]").forEach((slot) => {
      const index = Number(slot.getAttribute("data-ingredient"));
      if (!templates[index]) templates[index] = slot.firstElementChild.cloneNode(true);
    });
    const slots = new Map();
    stage.querySelectorAll("[data-vi]").forEach((slot) => slots.set(Number(slot.getAttribute("data-vi")), slot));

    let active = 0;
    let isDesktop = LV.isDesktop();

    function render() {
      const maxVisible = isDesktop ? 3 : 4;
      const stepPx = isDesktop ? 160 : 180;
      const opacityPerStep = isDesktop ? 0.38 : 0.2;

      for (const [vi, slot] of slots) {
        if (vi < active - maxVisible || vi > active + maxVisible) {
          slot.remove();
          slots.delete(vi);
        }
      }

      let previous = null;
      for (let vi = active - maxVisible; vi <= active + maxVisible; vi++) {
        const offset = vi - active;
        const abs = Math.abs(offset);
        let slot = slots.get(vi);
        if (!slot) {
          slot = document.createElement("div");
          slot.setAttribute("data-vi", String(vi));
          slot.setAttribute("data-ingredient", String(mod(vi, length)));
          const template = templates[mod(vi, length)];
          if (template) slot.appendChild(template.cloneNode(true));
          slots.set(vi, slot);
        }
        // Keep DOM order equal to the virtual order.
        const expectedNext = previous ? previous.nextElementSibling : stage.firstElementChild;
        if (slot !== expectedNext) stage.insertBefore(slot, expectedNext);
        previous = slot;

        slot.style.position = "absolute";
        slot.style.transform = `translateX(${offset * stepPx}px) rotateY(${-offset * 28}deg) translateZ(${-abs * 70}px) scale(${1 - abs * 0.08})`;
        slot.style.opacity = String(Math.max(0, 1 - abs * opacityPerStep));
        slot.style.zIndex = String(20 - abs);
        slot.style.transition = TRANSITION;
        slot.style.cursor = offset !== 0 ? "pointer" : "default";
      }

      // c-slider-dots.js loads after this file; the first (desktop) render
      // runs before it, while the server-rendered dots are already right.
      LV.sliderDots?.set(dots, mod(active, length));
    }

    const go = (next) => {
      active = next;
      render();
    };

    /* Touch swipe */
    let touchStartX = null;
    stage.addEventListener(
      "touchstart",
      (event) => {
        touchStartX = event.touches[0].clientX;
      },
      { passive: true },
    );
    stage.addEventListener("touchend", (event) => {
      if (touchStartX === null) return;
      const delta = touchStartX - event.changedTouches[0].clientX;
      if (Math.abs(delta) > 40) go(delta > 0 ? active + 1 : active - 1);
      touchStartX = null;
    });

    /* Mouse drag */
    let mouseStartX = null;
    let dragging = false;
    let dragDelta = 0;
    const resetDrag = () => {
      mouseStartX = null;
      dragging = false;
      dragDelta = 0;
      stage.style.cursor = "grab";
    };
    stage.addEventListener("mousedown", (event) => {
      if (event.target.closest("[data-ingredient-read-more]")) return;
      event.preventDefault();
      mouseStartX = event.clientX;
      dragging = false;
      dragDelta = 0;
      stage.style.cursor = "grabbing";
    });
    stage.addEventListener("mousemove", (event) => {
      if (mouseStartX === null) return;
      dragDelta = mouseStartX - event.clientX;
      if (Math.abs(dragDelta) > 5) dragging = true;
    });
    // The click that follows a drag lands on the card under the cursor and
    // would jump to it, undoing the swipe. Swallow it.
    let suppressClick = false;
    stage.addEventListener("mouseup", () => {
      if (mouseStartX === null) return;
      if (dragging) {
        suppressClick = true;
        setTimeout(() => {
          suppressClick = false;
        }, 0);
        if (Math.abs(dragDelta) > 40) go(dragDelta > 0 ? active + 1 : active - 1);
      }
      resetDrag();
    });
    stage.addEventListener("mouseleave", resetDrag);
    stage.addEventListener("dragstart", (event) => event.preventDefault());

    /* Wheel (non-passive so it can preventDefault), throttled */
    let lastWheel = 0;
    stage.addEventListener(
      "wheel",
      (event) => {
        // Product-page vertical scrolling should continue through the carousel.
        if (root.closest(".lavendr-product") && LV.isDesktop() && Math.abs(event.deltaY) >= Math.abs(event.deltaX)) return;
        const now = Date.now();
        if (now - lastWheel < 250) return;
        const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        if (Math.abs(delta) < 10) return;
        event.preventDefault();
        lastWheel = now;
        go(delta > 0 ? active + 1 : active - 1);
      },
      { passive: false },
    );

    /* Card clicks, Read More, dots */
    stage.addEventListener("click", (event) => {
      const more = event.target.closest("[data-ingredient-read-more]");
      if (more) {
        event.stopPropagation();
        openDetail(more.getAttribute("data-ingredient-read-more"));
        return;
      }
      const slot = event.target.closest("[data-vi]");
      if (!slot || dragging || suppressClick) return;
      const vi = Number(slot.getAttribute("data-vi"));
      if (vi !== active) go(vi);
    });
    dots?.addEventListener("click", (event) => {
      const i = LV.sliderDots ? LV.sliderDots.index(event) : -1;
      if (i >= 0) go(active - mod(active, length) + i);
    });

    /* Detail dialogs */
    const details = Array.from(root.querySelectorAll("[data-ingredient-detail]"));
    let openPanel = null;
    const onKey = (event) => {
      if (event.key === "Escape") closeDetail();
    };
    function openDetail(index) {
      const dialog = details.find((el) => el.getAttribute("data-ingredient-detail") === String(index));
      if (!dialog) return;
      // On <body>, outside the section's stacking context, so the overlay
      // also covers the sticky header and announcement bar.
      if (dialog.parentElement !== document.body) document.body.appendChild(dialog);
      clearTimeout(dialog._closeTimer);
      dialog.hidden = false;
      void dialog.offsetWidth; // start the fade from opacity 0
      dialog.setAttribute("data-open", "");
      openPanel = dialog;
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    }
    function closeDetail() {
      if (!openPanel) return;
      const dialog = openPanel;
      openPanel = null;
      dialog.removeAttribute("data-open");
      // Hide once the fade-out has finished.
      dialog._closeTimer = setTimeout(() => {
        dialog.hidden = true;
      }, window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 300);
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    }
    details.forEach((dialog) => {
      dialog.addEventListener("click", (event) => {
        if (event.target.closest("[data-ingredient-detail-close]") || !event.target.closest("[data-ingredient-detail-panel]")) {
          closeDetail();
        }
      });
    });

    window.addEventListener("resize", () => {
      const next = LV.isDesktop();
      if (next === isDesktop) return;
      isDesktop = next;
      render();
    });

    if (isDesktop) render();
  });
})();

/* ── c-lazy-video.js ── */
/* LazyVideo (snippets/lazy-video.liquid): assign src and play once the video nears the viewport. */
(function () {
  const videos = document.querySelectorAll("video[data-lazy-video]");
  if (!videos.length) return;

  const activate = (video) => {
    const src = video.getAttribute("data-lazy-video");
    if (!src) return;
    const absolute = new URL(src, window.location.href).href;
    if (video.currentSrc !== absolute && video.src !== absolute) {
      video.src = src;
      video.load();
    }
    video.muted = true;
    const p = video.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        /* Autoplay may be blocked; that's fine. */
      });
    }
  };

  videos.forEach((video) => {
    if (typeof IntersectionObserver === "undefined") {
      activate(video);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            observer.disconnect();
            activate(video);
            break;
          }
        }
      },
      { rootMargin: video.getAttribute("data-root-margin") || "400px", threshold: 0 },
    );
    observer.observe(video);
  });
})();

/* ── c-lp4-review-carousel.js ── */
/* LP4ReviewCarousel (snippets/lp4-review-carousel.liquid): active dot follows scroll, dots scroll to a card. */
(function () {
  const carousels = document.querySelectorAll("[data-lp4-carousel]");
  if (!carousels.length) return;

  carousels.forEach((root) => {
    const track = root.querySelector("[data-lp4-track]");
    const dots = Array.from(root.querySelectorAll("[data-lp4-dot]"));
    if (!track) return;

    const setActive = (index) => {
      dots.forEach((dot, i) => {
        dot.classList.toggle("bg-accent-1", i === index);
        dot.classList.toggle("bg-foreground/15", i !== index);
      });
    };

    track.addEventListener(
      "scroll",
      () => {
        const scrollLeft = track.scrollLeft;
        let closest = 0;
        let minDist = Infinity;
        Array.from(track.children).forEach((child, i) => {
          const dist = Math.abs(child.offsetLeft - scrollLeft);
          if (dist < minDist) {
            minDist = dist;
            closest = i;
          }
        });
        setActive(closest);
      },
      { passive: true },
    );

    dots.forEach((dot, i) => {
      dot.addEventListener("click", () => {
        const child = track.children[i];
        if (child) track.scrollTo({ left: child.offsetLeft, behavior: "smooth" });
      });
    });
  });
})();

/* ── c-new-moms-social-proof.js ── */
/* NewMomsSocialProof (snippets/new-moms-social-proof.liquid): pill dots follow the swiped card. */
(function () {
  const roots = document.querySelectorAll("[data-new-moms]");
  if (!roots.length) return;

  roots.forEach((root) => {
    const track = root.querySelector("[data-new-moms-track]");
    const dots = Array.from(root.querySelectorAll("[data-new-moms-dot]"));
    if (!track) return;

    const setActive = (index) => {
      dots.forEach((dot, i) => {
        const on = i === index;
        dot.classList.toggle("w-5", on);
        dot.classList.toggle("bg-foreground", on);
        dot.classList.toggle("w-2", !on);
        dot.classList.toggle("bg-foreground/25", !on);
        if (on) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
    };

    track.addEventListener(
      "scroll",
      () => {
        const trackLeft = track.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        Array.from(track.children).forEach((child, index) => {
          const distance = Math.abs(child.getBoundingClientRect().left - trackLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        setActive(closestIndex);
      },
      { passive: true },
    );

    dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        track.children[index]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
      });
    });
  });
})();

/* ── c-nutrition-label.js ── */
/*
 * NutritionLabelButton + NutritionLabelModal (snippets/nutrition-label-button.liquid,
 * snippets/nutrition-label-modal.liquid). Any [data-nutrition-label-open] opens
 * the single [data-nutrition-label-modal] on the page.
 */
(function () {
  let modal = null;
  let panel = null;
  let closing = false;
  let closeTimer = null;
  let lastFocused = null;
  let previousOverflow = "";

  function onKey(event) {
    if (event.key === "Escape") close();
    if (event.key === "Tab") {
      const stops = Array.from(panel.querySelectorAll('button, [href], input, select, textarea, [tabindex="0"]'));
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function setAnim(backdrop, image) {
    modal.classList.remove("lightbox-backdrop-in", "lightbox-backdrop-out");
    panel.classList.remove("lightbox-image-in", "lightbox-image-out");
    modal.classList.add(backdrop);
    panel.classList.add(image);
  }

  function open(trigger) {
    modal = modal || document.querySelector("[data-nutrition-label-modal]");
    if (!modal) return;
    panel = modal.querySelector("[data-nutrition-label-panel]");
    window.clearTimeout(closeTimer);
    lastFocused = trigger;
    if (modal.hidden) previousOverflow = document.body.style.overflow;
    // Portal: escape <main>'s stacking context so it sits above the header.
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
    closing = false;
    setAnim("lightbox-backdrop-in", "lightbox-image-in");
    modal.hidden = false;
    modal.querySelector(".nutrition-dialog__body").scrollTop = 0;
    document.body.style.overflow = "hidden";
    modal.querySelector("[data-nutrition-label-close]").focus({ preventScroll: true });
    window.addEventListener("keydown", onKey);
  }

  function close() {
    if (!modal || modal.hidden || closing) return;
    closing = true;
    setAnim("lightbox-backdrop-out", "lightbox-image-out");
    window.removeEventListener("keydown", onKey);
    closeTimer = window.setTimeout(() => {
      modal.hidden = true;
      closing = false;
      document.body.style.overflow = previousOverflow;
      lastFocused?.focus({ preventScroll: true });
    }, 200);
  }

  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-nutrition-label-open]")) {
      event.preventDefault();
      open(event.target.closest("[data-nutrition-label-open]"));
      return;
    }
    if (!modal || modal.hidden) return;
    if (event.target.closest("[data-nutrition-label-close]") || event.target === modal) close();
  });
})();

/* ── c-offer-countdown-banner.js ── */
/* OfferCountdownBanner (snippets/offer-countdown-banner.liquid): rolling 2-hour countdown. */
(function () {
  const LV = (window.Lavendr = window.Lavendr || {});
  const banners = document.querySelectorAll("[data-offer-countdown]");
  if (!banners.length) return;

  /* Same guarded helpers as c-sticky-shop-bar.js ("$140" -> active currency). */
  LV.localizeCopy =
    LV.localizeCopy ||
    ((text) =>
      String(text).replace(/\$([\d,]+(?:\.\d+)?)/g, (_, value) =>
        LV.money(LV.localCents(Number(value.replaceAll(",", "")))),
      ));
  LV.localizeCopyIn =
    LV.localizeCopyIn ||
    ((root) => {
      root.querySelectorAll("[data-local-copy]").forEach((el) => {
        const next = LV.localizeCopy(el.getAttribute("data-local-copy"));
        if (el.textContent !== next) el.textContent = next;
      });
    });

  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  const pad2 = (n) => String(n).padStart(2, "0");
  const clocks = [];
  banners.forEach((banner) => {
    LV.localizeCopyIn(banner);
    banner.querySelectorAll("[data-countdown-clock]").forEach((clock) => {
      clocks.push({
        clock,
        h: clock.querySelector("[data-countdown-h]"),
        m: clock.querySelector("[data-countdown-m]"),
        s: clock.querySelector("[data-countdown-s]"),
      });
    });
  });

  const tick = () => {
    const remaining = TWO_HOURS_MS - (Date.now() % TWO_HOURS_MS);
    const totalSec = Math.floor(remaining / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    for (const c of clocks) {
      c.h.textContent = pad2(h);
      c.m.textContent = pad2(m);
      c.s.textContent = pad2(s);
      c.clock.setAttribute("aria-label", `Offer ends in ${h} hours, ${m} minutes, ${s} seconds`);
    }
  };
  tick();
  window.setInterval(tick, 1000);
})();

/* ── c-phone-carousel.js ── */
/* Phone video carousel (sections/phone-videos.liquid): the active video plays in the phone, neighbours are cards. */
(function () {
  document.querySelectorAll("[data-phone-carousel]").forEach((root) => {
    const videos = (root.getAttribute("data-videos") || "").split("|").filter(Boolean);
    const n = videos.length;
    if (!n) return;
    const main = root.querySelector("[data-phone-video]");
    const playBtn = root.querySelector("[data-phone-play]");
    const cards = Array.from(root.querySelectorAll(".phone-videos__card"));
    let active = 0;

    const still = (url) => url + (url.includes("#") ? "" : "#t=0.1");
    const mod = (a) => ((a % n) + n) % n;

    function setVideo(video, url) {
      const next = still(url);
      if (video.getAttribute("data-src") === next) return;
      video.setAttribute("data-src", next);
      video.src = next;
    }

    const soundBtn = root.querySelector("[data-phone-sound]");
    let visible = false;
    let soundOn = false; // set once the shopper has tapped for sound

    // Show the play button when paused, the sound badge while playing muted.
    function syncUi() {
      playBtn.hidden = !main.paused;
      soundBtn.hidden = main.paused || !main.muted;
    }

    function playMain() {
      main.muted = !soundOn;
      const p = main.play();
      if (p && p.catch) {
        p.catch(() => {
          // Browsers only allow muted autoplay.
          main.muted = true;
          main.play().catch(() => {});
        });
      }
    }

    function render() {
      main.pause();
      setVideo(main, videos[active]);
      cards.forEach((card) => {
        const offset = Number(card.getAttribute("data-slot"));
        const index = mod(active + offset);
        card.classList.add("is-swapping");
        setTimeout(() => {
          setVideo(card.querySelector("video"), videos[index]);
          card.classList.remove("is-swapping");
        }, 180);
      });
      if (visible) playMain();
      syncUi();
    }

    function go(step) {
      active = mod(active + step);
      render();
    }

    ["play", "pause", "volumechange", "ended"].forEach((type) => main.addEventListener(type, syncUi));

    playBtn.addEventListener("click", () => {
      playMain();
    });
    soundBtn.addEventListener("click", () => {
      soundOn = true;
      main.muted = false;
    });
    main.addEventListener("click", () => {
      if (main.paused) {
        playMain();
      } else if (main.muted) {
        soundOn = true;
        main.muted = false;
      } else {
        main.pause();
      }
    });

    // Autoplay the live video while the section is on screen (about a third
    // of it in view). Scroll-based so it also works where IntersectionObserver
    // callbacks are throttled.
    function checkVisible() {
      const rect = root.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const shown = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
      const next = shown > Math.min(rect.height, vh) * 0.35;
      if (next === visible) return;
      visible = next;
      if (visible) playMain();
      else main.pause();
    }
    window.addEventListener("scroll", checkVisible, { passive: true });
    window.addEventListener("resize", checkVisible);

    root.querySelectorAll("[data-dir]").forEach((btn) => {
      btn.addEventListener("click", () => go(Number(btn.getAttribute("data-dir"))));
    });
    cards.forEach((card) => {
      card.addEventListener("click", () => go(Number(card.getAttribute("data-slot"))));
    });

    // Mouse drag / touch swipe across the section.
    let startX = null;
    root.addEventListener("pointerdown", (event) => {
      if (event.target.closest("button")) return;
      startX = event.clientX;
    });
    window.addEventListener("pointerup", (event) => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      startX = null;
      if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
    });

    render();
    checkVisible();
  });
})();

/* ── c-product-image-gallery.js ── */
/* ProductImageGallery (snippets/product-image-gallery.liquid): slides, thumbnails, arrows, keys. */
(function () {
  const behavior = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth";
  const ON = ["border-accent-1"];
  const OFF = ["border-transparent", "opacity-60", "hover:opacity-100"];

  document.querySelectorAll("[data-product-gallery]").forEach((root) => {
    const track = root.querySelector("[data-product-gallery-track]");
    const strip = root.querySelector("[data-product-gallery-thumbs]");
    if (!track) return;
    const thumbs = Array.from(root.querySelectorAll("[data-product-gallery-thumb]"));
    const count = track.children.length;
    let active = 0;

    function goTo(index) {
      const next = (index + count) % count;
      track.scrollTo({ left: next * track.clientWidth, behavior: behavior() });
    }

    function revealThumb() {
      const selected = thumbs[active];
      if (!strip || !selected) return;
      const left = selected.offsetLeft;
      const right = left + selected.offsetWidth;
      if (left < strip.scrollLeft || right > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({
          left: left - (strip.clientWidth - selected.offsetWidth) / 2,
          behavior: behavior(),
        });
      }
    }

    function setActive(index) {
      if (index === active) return;
      active = index;
      thumbs.forEach((thumb, i) => {
        const on = i === index;
        ON.forEach((c) => thumb.classList.toggle(c, on));
        OFF.forEach((c) => thumb.classList.toggle(c, !on));
        if (on) thumb.setAttribute("aria-current", "true");
        else thumb.removeAttribute("aria-current");
      });
      revealThumb();
    }

    track.addEventListener(
      "scroll",
      () => {
        if (!track.clientWidth) return;
        setActive(Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / track.clientWidth))));
      },
      { passive: true },
    );
    track.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      goTo(active + (event.key === "ArrowRight" ? 1 : -1));
    });
    root.querySelectorAll("[data-product-gallery-step]").forEach((button) => {
      button.addEventListener("click", () => goTo(active + Number(button.getAttribute("data-product-gallery-step"))));
    });
    thumbs.forEach((thumb, i) => thumb.addEventListener("click", () => goTo(i)));
  });
})();

/* ── c-skin-review-carousel.js ── */
/* SkinReviewCarousel (snippets/skin-review-carousel.liquid): active slide, slider dots, arrow keys. */
(function () {
  const LV = (window.Lavendr = window.Lavendr || {});
  const reduce = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  document.querySelectorAll("[data-skin-reviews]").forEach((root) => {
    const track = root.querySelector("[data-skin-reviews-track]");
    const dots = root.querySelector("[data-slider-dots]");
    if (!track) return;
    const slides = () => Array.from(track.children);
    let active = 0;

    function setActive(index) {
      if (index === active) return;
      active = index;
      LV.sliderDots?.set(dots, index);
    }

    function goTo(index) {
      const slide = slides()[index];
      if (!slide) return;
      stopGlide();
      track.scrollTo({ left: slide.offsetLeft, behavior: reduce() ? "instant" : "smooth" });
    }

    track.addEventListener(
      "scroll",
      () => {
        const items = slides();
        let closest = 0;
        items.forEach((child, index) => {
          if (
            Math.abs(child.offsetLeft - track.scrollLeft) <
            Math.abs(items[closest].offsetLeft - track.scrollLeft)
          ) {
            closest = index;
          }
        });
        setActive(closest);
      },
      { passive: true },
    );

    // Mouse drag-to-swipe (touch and trackpads already scroll natively).
    // Free-flowing like a trackpad: the cards follow the pointer, glide on
    // with the release speed and stop where they land, no snapping back.
    // The track has no scroll snap for fine pointers (pointer-fine:snap-none).
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0; // scroll px/ms, positive = dragging right
    let scale = 1; // pointer px per scroll px (page zoom)
    let glideFrame = 0;
    track.style.cursor = "grab";

    function stopGlide() {
      cancelAnimationFrame(glideFrame);
      glideFrame = 0;
    }

    function glide() {
      if (reduce() || Math.abs(velocity) < 0.05) return;
      let last = performance.now();
      const step = (now) => {
        const dt = Math.min(32, now - last);
        last = now;
        const before = track.scrollLeft;
        track.scrollLeft = before - velocity * dt;
        velocity *= Math.pow(0.95, dt / 16); // friction
        const hitEdge = track.scrollLeft === before;
        glideFrame = Math.abs(velocity) > 0.02 && !hitEdge ? requestAnimationFrame(step) : 0;
      };
      glideFrame = requestAnimationFrame(step);
    }

    track.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      if (event.target.closest("a, button, input, select, textarea")) return;
      stopGlide();
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      lastX = event.clientX;
      lastT = performance.now();
      velocity = 0;
      // Pointer coordinates are in screen px; scrollLeft is in the track's own
      // px, which differ under CSS zoom (tools/zoom-css.mjs).
      scale = track.offsetWidth ? track.getBoundingClientRect().width / track.offsetWidth : 1;
    });
    track.addEventListener("wheel", stopGlide, { passive: true });

    window.addEventListener("pointermove", (event) => {
      if (!dragging) return;
      const dx = (event.clientX - startX) / scale;
      if (!moved && Math.abs(dx) < 4) return;
      if (!moved) {
        moved = true;
        track.style.cursor = "grabbing";
        track.style.userSelect = "none";
      }
      track.scrollLeft = startScroll - dx;
      const now = performance.now();
      if (now > lastT) {
        // Smoothed so one jittery event doesn't decide the glide.
        velocity = velocity * 0.6 + ((event.clientX - lastX) / scale / (now - lastT)) * 0.4;
        lastX = event.clientX;
        lastT = now;
      }
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      if (!moved) return;
      track.style.cursor = "grab";
      track.style.userSelect = "";
      // A pause before letting go means "stop here", not a flick.
      if (performance.now() - lastT > 80) velocity = 0;
      glide();
    }
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);

    // A drag must not count as a click on whatever is under the pointer.
    track.addEventListener(
      "click",
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }
      },
      true,
    );
    track.addEventListener("dragstart", (event) => event.preventDefault());

    track.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
      event.preventDefault();
      const last = slides().length - 1;
      goTo(Math.max(0, Math.min(last, active + (event.key === "ArrowRight" ? 1 : -1))));
    });

    // LV.sliderDots comes from c-slider-dots.js, which loads after this file.
    dots?.addEventListener("click", (event) => {
      const index = LV.sliderDots ? LV.sliderDots.index(event) : -1;
      if (index >= 0) goTo(index);
    });
  });
})();

/* ── c-slider-dots.js ── */
/*
 * SliderDots (snippets/slider-dots.liquid), ported from
 * src/components/SliderDots.tsx.
 *
 * LV.sliderDots.set(dots, index)  moves the active dot ([data-slider-dots]).
 * LV.sliderDots.index(event)      index of the clicked dot, or -1.
 * Components that load before this file call them from event handlers only.
 *
 * useSnapTrack: a [data-snap-slider] root with a [data-snap-track] and a
 * [data-slider-dots] (product reasons, video reviews) is wired here.
 */
(function () {
  const LV = (window.Lavendr = window.Lavendr || {});
  const ON = ["w-5", "bg-accent-1"];
  const OFF = ["w-2", "bg-[#2d2e2d]/20", "hover:bg-[#2d2e2d]/40"];

  function set(dots, index) {
    if (!dots) return;
    dots.querySelectorAll("[data-slider-dot]").forEach((button, i) => {
      const on = i === index;
      if (on) button.setAttribute("aria-current", "true");
      else button.removeAttribute("aria-current");
      const pip = button.firstElementChild;
      if (!pip) return;
      ON.forEach((c) => pip.classList.toggle(c, on));
      OFF.forEach((c) => pip.classList.toggle(c, !on));
    });
  }

  function index(event) {
    const button = event.target.closest("[data-slider-dot]");
    return button ? Number(button.getAttribute("data-slider-dot")) : -1;
  }

  LV.sliderDots = { set, index };

  /* useSnapTrack */
  document.querySelectorAll("[data-snap-slider]").forEach((root) => {
    const track = root.querySelector("[data-snap-track]");
    const dots = root.querySelector("[data-slider-dots]");
    if (!track || !dots) return;
    const slides = () => Array.from(track.children);
    const padding = () => parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    let active = 0;

    const setActive = (next) => {
      if (next === active) return;
      active = next;
      set(dots, next);
    };

    track.addEventListener(
      "scroll",
      () => {
        const items = slides();
        if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) {
          setActive(items.length - 1);
          return;
        }
        const origin = track.getBoundingClientRect().left;
        const pad = padding();
        let closest = 0;
        let best = Infinity;
        items.forEach((slide, i) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - origin - pad);
          if (distance < best) {
            best = distance;
            closest = i;
          }
        });
        setActive(closest);
      },
      { passive: true },
    );

    dots.addEventListener("click", (event) => {
      const slide = slides()[index(event)];
      if (!slide) return;
      track.scrollTo({
        left: track.scrollLeft + slide.getBoundingClientRect().left - track.getBoundingClientRect().left - padding(),
        behavior: "smooth",
      });
    });
  });
})();

/* ── c-sticky-shop-bar.js ── */
/* StickyShopBar (snippets/sticky-shop-bar.liquid): slide in past 300px, hide while a target is in view. */
(function () {
  const LV = (window.Lavendr = window.Lavendr || {});

  /** "$140" -> active-currency amount, like localizeCopy() in the React components. */
  LV.localizeCopy =
    LV.localizeCopy ||
    ((text) =>
      String(text).replace(/\$([\d,]+(?:\.\d+)?)/g, (_, value) =>
        LV.money(LV.localCents(Number(value.replaceAll(",", "")))),
      ));
  /* Idempotent: the source copy lives in the attribute, so any script may re-run this. */
  LV.localizeCopyIn =
    LV.localizeCopyIn ||
    ((root) => {
      root.querySelectorAll("[data-local-copy]").forEach((el) => {
        const next = LV.localizeCopy(el.getAttribute("data-local-copy"));
        if (el.textContent !== next) el.textContent = next;
      });
    });

  const bars = document.querySelectorAll("[data-sticky-shop-bar]");
  if (!bars.length) return;

  bars.forEach((bar) => {
    LV.localizeCopyIn(bar);
    if (bar.hasAttribute("data-always-visible")) return;

    let scrolledPastHero = false;
    let hideTargetInView = false;
    const render = () => {
      const visible = scrolledPastHero && !hideTargetInView;
      bar.classList.toggle("translate-y-0", visible);
      bar.classList.toggle("translate-y-full", !visible);
    };

    const onScroll = () => {
      const next = window.scrollY > 300;
      if (next === scrolledPastHero) return;
      scrolledPastHero = next;
      render();
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const selector = bar.getAttribute("data-hide-when");
    const target = selector ? document.querySelector(selector) : null;
    if (target && "IntersectionObserver" in window) {
      new IntersectionObserver(
        ([entry]) => {
          hideTargetInView = entry.isIntersecting;
          render();
        },
        { rootMargin: "0px 0px -15% 0px", threshold: 0 },
      ).observe(target);
    }
  });
})();

/* ── p-lp1.js ── */
/* lp1/lp2/lp3 IngredientCard (snippets/lp1-ingredient-card.liquid): each card toggles its science panel. */
(function () {
  if (!document.querySelector("[data-lp1-ingredient]")) return;

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-lp1-ingredient-trigger]");
    if (!trigger) return;
    const card = trigger.closest("[data-lp1-ingredient]");
    if (!card) return;
    const open = trigger.getAttribute("aria-expanded") !== "true";
    trigger.setAttribute("aria-expanded", String(open));
    card.querySelector("[data-lp1-ingredient-chevron]")?.classList.toggle("rotate-180", open);
    const panel = card.querySelector("[data-lp1-ingredient-panel]");
    panel?.classList.toggle("grid-rows-[1fr]", open);
    panel?.classList.toggle("grid-rows-[0fr]", !open);
  });
})();

/* ── p-lp10.js ── */
/* LP10 (sections/page-lp10.liquid) and lp14: LP10StickyCta + DealEndingCountdown from src/app/lp10/LP10Extras.tsx. */
(function () {
  /* Sticky CTA (snippets/lp10-sticky-cta.liquid): visible past 300px, hidden while the target is in view. */
  document.querySelectorAll("[data-lp10-sticky-cta]").forEach((bar) => {
    const link = bar.querySelector("a");
    let scrolledPastHero = false;
    let targetInView = false;
    const render = () => {
      const visible = scrolledPastHero && !targetInView;
      bar.classList.toggle("translate-y-0", visible);
      bar.classList.toggle("translate-y-full", !visible);
      bar.setAttribute("aria-hidden", visible ? "false" : "true");
      if (link) {
        if (visible) link.removeAttribute("tabindex");
        else link.setAttribute("tabindex", "-1");
      }
    };
    const onScroll = () => {
      const next = window.scrollY > 300;
      if (next === scrolledPastHero) return;
      scrolledPastHero = next;
      render();
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const selector = bar.getAttribute("data-hide-when");
    const target = selector ? document.querySelector(selector) : null;
    if (target && "IntersectionObserver" in window) {
      new IntersectionObserver(
        ([entry]) => {
          targetInView = entry.isIntersecting;
          render();
        },
        { rootMargin: "0px 0px -15% 0px", threshold: 0 },
      ).observe(target);
    }
  });

  /* "Deal ending in: 01:59:59", rolling 2-hour countdown. */
  const clocks = document.querySelectorAll("[data-lp10-deal-clock]");
  if (!clocks.length) return;
  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  const pad2 = (n) => String(n).padStart(2, "0");
  const tick = () => {
    const totalSec = Math.floor((TWO_HOURS_MS - (Date.now() % TWO_HOURS_MS)) / 1000);
    const text = `${pad2(Math.floor(totalSec / 3600))}:${pad2(Math.floor((totalSec % 3600) / 60))}:${pad2(totalSec % 60)}`;
    clocks.forEach((clock) => {
      if (clock.textContent !== text) clock.textContent = text;
    });
  };
  tick();
  window.setInterval(tick, 1000);
})();

/* ── p-lp11.js ── */
/*
 * lp11 page JS. SkinStats (snippets/lp11-skin-stats.liquid, also rendered on
 * lp4 and, through lp12-bloat-stats, on lp12): once 15% of the list is in
 * view, every ring fills to its percent and the label counts up
 * (1.4s, ease-out cubic), like the React component.
 */
(function () {
  const lists = document.querySelectorAll("[data-lp11-stats]");
  if (!lists.length) return;

  function render(list, progress) {
    list.querySelectorAll("[data-lp11-stat-ring]").forEach((ring) => {
      const percent = Number(ring.getAttribute("data-lp11-stat-ring"));
      ring.setAttribute("stroke-dashoffset", String(100 - percent * progress));
    });
    list.querySelectorAll("[data-lp11-stat-value]").forEach((label) => {
      const percent = Number(label.getAttribute("data-lp11-stat-value"));
      label.textContent = `${Math.round(percent * progress)}%`;
    });
  }

  function run(list) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      render(list, 1);
      return;
    }
    let start;
    const animate = (now) => {
      if (start === undefined) start = now;
      const elapsed = Math.min((now - start) / 1400, 1);
      render(list, 1 - Math.pow(1 - elapsed, 3));
      if (elapsed < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }

  lists.forEach((list) => {
    if (typeof IntersectionObserver === "undefined") {
      render(list, 1);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        run(list);
      },
      { threshold: 0.15 },
    );
    observer.observe(list);
  });
})();

/* ── p-lp12.js ── */
/*
 * lp12 page JS.
 * - ReviewCarousel (snippets/lp12-review-carousel.liquid): active slide,
 *   prev/next arrows and arrow keys, like src/app/lp12/ReviewCarousel.tsx.
 * - IngredientsSection (snippets/lp12-ingredients-section.liquid): each card
 *   toggles its science panel.
 * (BloatStats reuses the lp11 ring stats: frontend/js/p-lp11.js.)
 */
(function () {
  const reduce = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  document.querySelectorAll("[data-lp12-reviews]").forEach((root) => {
    const track = root.querySelector("[data-lp12-reviews-track]");
    const prev = root.querySelector("[data-lp12-reviews-prev]");
    const next = root.querySelector("[data-lp12-reviews-next]");
    const current = root.querySelector("[data-lp12-reviews-current]");
    if (!track) return;
    const slides = () => Array.from(track.children);
    let active = 0;

    function setActive(index) {
      active = index;
      if (current) current.textContent = String(index + 1);
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides().length - 1;
    }

    function goTo(index) {
      const slide = slides()[index];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft, behavior: reduce() ? "instant" : "smooth" });
    }

    track.addEventListener(
      "scroll",
      () => {
        const items = slides();
        let closest = 0;
        items.forEach((child, index) => {
          if (
            Math.abs(child.offsetLeft - track.scrollLeft) <
            Math.abs(items[closest].offsetLeft - track.scrollLeft)
          ) {
            closest = index;
          }
        });
        setActive(closest);
      },
      { passive: true },
    );

    track.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
      event.preventDefault();
      const last = slides().length - 1;
      goTo(Math.max(0, Math.min(last, active + (event.key === "ArrowRight" ? 1 : -1))));
    });

    prev?.addEventListener("click", () => goTo(active - 1));
    next?.addEventListener("click", () => goTo(active + 1));
  });

  if (document.querySelector("[data-lp12-ingredient]")) {
    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-lp12-ingredient-trigger]");
      if (!trigger) return;
      const card = trigger.closest("[data-lp12-ingredient]");
      const open = trigger.getAttribute("aria-expanded") !== "true";
      trigger.setAttribute("aria-expanded", String(open));
      card.querySelector("[data-lp12-ingredient-chevron]")?.classList.toggle("rotate-180", open);
      const panel = card.querySelector("[data-lp12-ingredient-panel]");
      panel?.classList.toggle("grid-rows-[1fr]", open);
      panel?.classList.toggle("grid-rows-[0fr]", !open);
    });
  }
})();

/* ── p-lp13.js ── */
/* LP13 (sections/page-lp13.liquid): "$N" copy in the active currency, and the
   SkinStats rings (snippets/lp13-skin-stats.liquid) that fill once 15% is in view. */
(function () {
  const LV = (window.Lavendr = window.Lavendr || {});
  const root = document.querySelector("[data-lp13]");
  if (!root) return;

  // localizeLp13Copy(): LV.localizeCopyIn comes from c-sticky-shop-bar.js.
  if (LV.localizeCopyIn) LV.localizeCopyIn(root);

  root.querySelectorAll("[data-lp13-skin-stats]").forEach((section) => {
    const rings = [...section.querySelectorAll("[data-stat-ring]")].map((ring) => ({
      ring,
      percent: Number(ring.getAttribute("data-stat-ring")),
      count: ring.closest("li").querySelector("[data-stat-count]"),
    }));
    const render = (progress) =>
      rings.forEach(({ ring, percent, count }) => {
        ring.setAttribute("stroke-dashoffset", String(100 - percent * progress));
        count.textContent = `${Math.round(percent * progress)}%`;
      });

    if (typeof IntersectionObserver === "undefined") return render(1);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        if (LV.prefersReducedMotion?.()) return render(1);
        let start;
        const animate = (now) => {
          start ??= now;
          const elapsed = Math.min((now - start) / 1400, 1);
          render(1 - Math.pow(1 - elapsed, 3));
          if (elapsed < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      },
      { threshold: 0.15 },
    );
    observer.observe(section);
  });
})();

/* ── p-lp16.js ── */
/* LP16 (sections/page-lp16.liquid): "$N" copy in the active currency, like localizeLp13Copy(). */
(function () {
  const LV = (window.Lavendr = window.Lavendr || {});
  const root = document.querySelector("[data-lp16]");
  if (!root) return;
  // LV.localizeCopyIn comes from c-sticky-shop-bar.js / c-offer-countdown-banner.js.
  if (LV.localizeCopyIn) LV.localizeCopyIn(root);
})();

/* ── p-lp2.js ── */
/* lp2/lp3 ReviewCarousel (snippets/lp2-review-carousel.liquid): active dot follows scroll, dots scroll to a slide. */
(function () {
  const carousels = document.querySelectorAll("[data-lp2-carousel]");
  if (!carousels.length) return;

  carousels.forEach((root) => {
    const track = root.querySelector("[data-lp2-track]");
    const dots = Array.from(root.querySelectorAll("[data-lp2-dot]"));
    if (!track) return;

    const setActive = (index) => {
      dots.forEach((dot, i) => {
        dot.classList.toggle("bg-accent-1", i === index);
        dot.classList.toggle("bg-foreground/15", i !== index);
      });
    };

    track.addEventListener(
      "scroll",
      () => {
        if (!track.offsetWidth) return;
        setActive(Math.round(track.scrollLeft / track.offsetWidth));
      },
      { passive: true },
    );

    dots.forEach((dot, i) => {
      dot.addEventListener("click", () => {
        track.scrollTo({ left: i * track.offsetWidth, behavior: "smooth" });
      });
    });
  });
})();

/* ── p-lp21.js ── */
/* LP21 deal timer (sections/page-lp21.liquid): same rolling 2-hour window as the countdown banner. */
(function () {
  const timers = document.querySelectorAll("[data-lp21-deal-timer]");
  if (!timers.length) return;
  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  const pad2 = (n) => String(n).padStart(2, "0");
  const tick = () => {
    const total = Math.floor((TWO_HOURS_MS - (Date.now() % TWO_HOURS_MS)) / 1000);
    const text = `${pad2(Math.floor(total / 3600))}:${pad2(Math.floor((total % 3600) / 60))}:${pad2(total % 60)}`;
    timers.forEach((timer) => {
      if (timer.textContent !== text) timer.textContent = text;
    });
  };
  tick();
  window.setInterval(tick, 1000);
})();

/* ── p-lp5.js ── */
/* LP5 (sections/page-lp5.liquid): localize "$140" in the desktop offer card, like the sticky bar does. */
(function () {
  const card = document.querySelector("[data-lp5-offer]");
  if (card && window.Lavendr?.localizeCopyIn) window.Lavendr.localizeCopyIn(card);
})();

/* ── p-lp6.js ── */
/* lp6/lp8/lp9 desktop offer card (snippets/lp6-desktop-offer.liquid): localize "$N" copy like the sticky shop bar. */
(function () {
  const LV = (window.Lavendr = window.Lavendr || {});
  const cards = document.querySelectorAll("[data-lp6-offer]");
  if (!cards.length || typeof LV.localizeCopyIn !== "function") return;
  cards.forEach((card) => LV.localizeCopyIn(card));
})();

/* ── p-lp9.js ── */
/* lp9 (sections/page-lp9.liquid): LP9Extras.tsx ported.
 * - [data-lp9-deal-countdown]: rolling 2-hour "Deal ending in" clock.
 * - [data-lp9-sticky]: bottom CTA shown past 300px scroll, hidden while the
 *   data-hide-when target (#lp9-finale) is in view. */
(function () {
  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  const pad2 = (n) => String(n).padStart(2, "0");

  const clocks = document.querySelectorAll("[data-lp9-deal-countdown]");
  if (clocks.length) {
    const tick = () => {
      const totalSec = Math.floor((TWO_HOURS_MS - (Date.now() % TWO_HOURS_MS)) / 1000);
      const text = `${pad2(Math.floor(totalSec / 3600))}:${pad2(Math.floor((totalSec % 3600) / 60))}:${pad2(totalSec % 60)}`;
      clocks.forEach((el) => {
        if (el.textContent !== text) el.textContent = text;
      });
    };
    tick();
    window.setInterval(tick, 1000);
  }

  document.querySelectorAll("[data-lp9-sticky]").forEach((bar) => {
    const link = bar.querySelector("a");
    let scrolledPastHero = false;
    let targetInView = false;
    const render = () => {
      const visible = scrolledPastHero && !targetInView;
      bar.classList.toggle("translate-y-0", visible);
      bar.classList.toggle("translate-y-full", !visible);
      bar.setAttribute("aria-hidden", visible ? "false" : "true");
      if (link) {
        if (visible) link.removeAttribute("tabindex");
        else link.setAttribute("tabindex", "-1");
      }
    };
    const onScroll = () => {
      const next = window.scrollY > 300;
      if (next === scrolledPastHero) return;
      scrolledPastHero = next;
      render();
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const selector = bar.getAttribute("data-hide-when");
    const target = selector ? document.querySelector(selector) : null;
    if (target && "IntersectionObserver" in window) {
      new IntersectionObserver(
        ([entry]) => {
          targetInView = entry.isIntersecting;
          render();
        },
        { rootMargin: "0px 0px -15% 0px", threshold: 0 },
      ).observe(target);
    }
  });
})();

/* ── p-product.js ── */
/* Product page (sections/main-product.liquid): review photo lightbox.
   (The five reasons carousel uses the shared slider dots: c-slider-dots.js.) */
(function () {
  const dialog = document.querySelector("[data-review-lightbox-dialog]");
  if (!dialog) return;
  const panel = dialog.querySelector("[data-review-lightbox-panel]");
  const image = dialog.querySelector("[data-review-lightbox-image]");
  let closing = false;

  const setAnim = (backdrop, img) => {
    dialog.classList.remove("lightbox-backdrop-in", "lightbox-backdrop-out");
    panel.classList.remove("lightbox-image-in", "lightbox-image-out");
    dialog.classList.add(backdrop);
    panel.classList.add(img);
  };
  const onKey = (event) => {
    if (event.key === "Escape") close();
  };

  function open(src) {
    // Rendered in place like the React lightbox (no portal), so the sticky
    // header stays above it exactly as on the old site.
    image.src = src;
    closing = false;
    setAnim("lightbox-backdrop-in", "lightbox-image-in");
    dialog.hidden = false;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
  }

  function close() {
    if (dialog.hidden || closing) return;
    closing = true;
    setAnim("lightbox-backdrop-out", "lightbox-image-out");
    window.setTimeout(() => {
      dialog.hidden = true;
      closing = false;
      image.removeAttribute("src");
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    }, 200);
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-review-lightbox]");
    if (trigger) {
      open(trigger.getAttribute("data-review-lightbox"));
      return;
    }
    if (dialog.hidden || !dialog.contains(event.target)) return;
    if (event.target.closest("[data-review-lightbox-close]") || !event.target.closest("[data-review-lightbox-panel]")) {
      close();
    }
  });
})();

/* ── p-quiz.js ── */
/*
 * /quiz funnel (sections/page-quiz.liquid). Port of src/app/quiz/QuizFunnel.tsx
 * and blocks.tsx: a multi-step client funnel where the "concern" answer picks
 * one of four avatar tracks (aging, dry, redness, tired), with a progress bar,
 * two timed loaders, a skin-type result and an offer that hands off to the
 * product page.
 *
 * Copy, options and personalization tables come from the JSON in
 * snippets/quiz-data.liquid (generated from content.ts, tracks.ts and
 * track-copy.ts); prices come from Liquid (data-savings / data-per-cup on the
 * root), so Shopify Markets converts them. The section renders the first
 * screen; this script renders every screen after it.
 *
 * Analytics: quiz_step / quiz_answer / quiz_complete go to LV.track (was
 * trackAnalyticsEvent). Every screen gets its own history entry with the same
 * URL, so Back walks one screen and the Meta pixel sends no extra PageViews.
 */
(function () {
  const root = document.querySelector("[data-quiz]");
  const dataEl = document.querySelector("script[data-quiz-data]");
  if (!root || !dataEl) return;

  let D;
  try {
    D = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }
  let IMAGES = {};
  try {
    IMAGES = JSON.parse(document.querySelector("script[data-quiz-images]")?.textContent || "{}");
  } catch {
    /* fall back to the raw paths */
  }

  const LV = window.Lavendr || {};
  const COPY = D.COPY;
  const QUIZ_ID = D.QUIZ_ID;
  const DESTINATION = root.getAttribute("data-product-url") || "/products/lavendr-blend";
  const SAVINGS = root.getAttribute("data-savings") || "";
  const PER_CUP = root.getAttribute("data-per-cup") || "";

  /** Screens on the longest path (aging track, life stage asked, something tried). */
  const LONGEST_PATH = 24;
  const STORAGE_KEY = "lavendr-quiz";
  /** Read by the email popup: quiz visitors never get the timed popup this session. */
  const QUIZ_VISITOR_KEY = "lavendr_quiz_visitor";
  const AUTO_ADVANCE_MS = 260;
  /** Answers that belong to one track, dropped when she switches concern. */
  const TRACK_QUESTIONS = ["problem", "stage", "tried", "went", "mirror"];
  const QUESTION_STEPS = new Set(D.QUESTIONS);
  const MULTI = { problem: true, tried: true };

  const track = (name, data) => {
    try {
      LV.track?.(name, data);
    } catch {
      /* analytics is best effort */
    }
  };

  /* ── Small helpers ─────────────────────────────────────────────── */

  const esc = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  /** "/assets/quiz/stage-1.webp" -> the theme asset URL of quiz-stage-1.webp. */
  const assetUrl = (src) => {
    const name = String(src).replace(/^\/assets\//, "").replace(/^\//, "").replace(/\//g, "-");
    return IMAGES[name] || src;
  };

  /** next/image stand-in (these images have no responsive variants, so no srcset). */
  const image = ({ src, alt = "", width, height, loading = "lazy" }) =>
    `<img alt="${esc(alt)}" width="${width}" height="${height}" decoding="async" loading="${loading}" style="color:transparent" src="${esc(assetUrl(src))}">`;

  const LIST =
    typeof Intl !== "undefined" && Intl.ListFormat
      ? new Intl.ListFormat("en", { type: "conjunction" })
      : {
          format: (items) =>
            items.length < 3
              ? items.join(" and ")
              : `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`,
        };

  /** "Vitamin C serums" -> "vitamin C serums": only the first letter drops. */
  const inSentence = (label) => label.charAt(0).toLowerCase() + label.slice(1);

  function formatToday() {
    const now = new Date();
    const day = now.getDate();
    const ordinal =
      day % 10 === 1 && day % 100 !== 11
        ? "st"
        : day % 10 === 2 && day % 100 !== 12
          ? "nd"
          : day % 10 === 3 && day % 100 !== 13
            ? "rd"
            : "th";
    return `${now.toLocaleDateString("en-US", { month: "short" })} ${day}${ordinal}`;
  }

  /* ── Tracks, options and the profile (content.ts / tracks.ts) ─── */

  const isPlainObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value);

  function deepMerge(base, override) {
    if (!isPlainObject(base) || !isPlainObject(override)) return override ?? base;
    const merged = { ...base };
    for (const [key, value] of Object.entries(override)) {
      merged[key] = key in base ? deepMerge(base[key], value) : value;
    }
    return merged;
  }

  const trackCache = new Map();

  /** The track a concern opens, with that concern's copy overrides merged in. */
  function trackFor(concernId) {
    const id = D.CONCERN_TRACK[concernId ?? ""] ?? "aging";
    const key = concernId || `#${id}`;
    if (trackCache.has(key)) return trackCache.get(key);
    const base = D.TRACKS[id];
    const copy = D.TRACK_COPY[id];
    const override = concernId ? copy.concerns?.[concernId] : undefined;
    const result = {
      ...base,
      copy: concernId && override ? deepMerge(copy, override) : copy,
      stagePhoto: (stageId) => `${base.stagePhotoPrefix}${stageId}.webp`,
    };
    trackCache.set(key, result);
    return result;
  }

  function asksLifeStage(answers) {
    return (answers.age?.[0] ?? "") in D.LIFE_STAGES_BY_AGE;
  }

  /** The options a question shows, given the track and earlier answers. */
  function optionsFor(question, answers) {
    const trk = trackFor(answers.concern?.[0]);
    switch (question) {
      case "problem":
        return trk.areas;
      case "stage":
        return trk.stages;
      case "tried":
        return trk.tried;
      case "mirror":
        return trk.copy.mirror.options;
      case "lifeStage": {
        const allowed = D.LIFE_STAGES_BY_AGE[answers.age?.[0] ?? ""] ?? [];
        return D.SHARED_OPTIONS.lifeStage.filter((option) => allowed.includes(option.id));
      }
      default:
        return D.SHARED_OPTIONS[question] || [];
    }
  }

  function labelsFor(question, answers) {
    const ids = answers[question];
    if (!ids) return [];
    const options = optionsFor(question, answers);
    return ids.map((id) => options.find((option) => option.id === id)?.label).filter(Boolean);
  }

  function buildProfile(answers) {
    const trk = trackFor(answers.concern?.[0]);
    const ageId = answers.age?.[0] ?? "";
    const known = ageId in D.AGE_BRACKET;
    const bracket = known ? D.AGE_BRACKET[ageId] : 3;
    const triedIds = answers.tried ?? [];
    const triedOptions = trk.tried.filter((option) => triedIds.includes(option.id));
    const triedLabels = triedOptions.map((option) => inSentence(option.label));
    const triedProducts = triedOptions
      .filter((option) => option.kind !== "nothing")
      .map((option) => inSentence(option.label));
    const triedNothing = triedOptions.some((option) => option.kind === "nothing");
    const stageId = answers.stage?.[0] ?? "stage-2";
    const lifeStage = answers.lifeStage?.[0] ?? "none";
    const areaIds = answers.problem ?? [];
    const areas = trk.areas.filter((option) => areaIds.includes(option.id));
    const covering = areas.find((option) => option.covers);
    const phrases = areas.map((option) => option.phrase);
    const problemAll = LIST.format(phrases) || trk.problemFallback;

    return {
      track: trk,
      trackId: trk.id,
      age: (labelsFor("age", answers)[0] ?? "your age").toLowerCase(),
      agePeers: D.AGE_PHRASES[ageId]?.peers ?? "women your age",
      ageOne: D.AGE_PHRASES[ageId]?.one ?? "a woman your age",
      ageWhen: D.AGE_WHEN[ageId] ?? "at your age",
      bracket,
      collagenWhen: known ? D.COLLAGEN_WHEN[bracket] : "by your 60s",
      collagenLoss: 100 - D.COLLAGEN_BARS[bracket].value,
      // Inside sentences a long list buries the point, so 4+ areas read as
      // "the first two and more". The reveal card still lists every area.
      problem: covering
        ? covering.phrase
        : phrases.length > 3
          ? LIST.format([...phrases.slice(0, 2), "more"])
          : problemAll,
      problemAll,
      tried: LIST.format(triedLabels) || "nothing yet",
      triedProducts: LIST.format(triedProducts) || "creams",
      time: D.TIME_PHRASE[answers.time?.[0] ?? ""] ?? "a while",
      stage: D.STAGE_RESULT[stageId] ?? D.STAGE_RESULT["stage-2"],
      lifeStage,
      lifeStagePhrase: trk.copy.lifeStagePhrase[lifeStage] ?? "",
      skinType: labelsFor("skinType", answers)[0] ?? "Not sure",
      coffee: answers.coffee?.[0] ?? "cantSkip",
      triedNothing,
      mechanism: triedNothing
        ? "nothing"
        : triedOptions.some((option) => option.kind === "collagen")
          ? "collagen"
          : "topical",
      triedTopical: triedOptions.some((option) => option.kind === "topical"),
      wentPositive: ["real-results", "small"].includes(answers.went?.[0] ?? ""),
      under40: ageId === "under-40",
      under50: ageId === "under-40" || ageId === "40s",
    };
  }

  /**
   * Replaces {token} placeholders with profile values, plus the extra values
   * ({savings}, {perCup} from Liquid, client-only {today}). An empty token
   * leaves no double space or space before a comma.
   */
  function fill(template, profile, extra) {
    const values = {
      age: profile.age,
      agePeers: profile.agePeers,
      ageOne: profile.ageOne,
      ageWhen: profile.ageWhen,
      problem: profile.problem,
      tried: profile.tried,
      triedProducts: profile.triedProducts,
      time: profile.time,
      stageOf3: profile.stage.of3,
      stageType: profile.stage.type,
      collagenWhen: profile.collagenWhen,
      collagenLoss: String(profile.collagenLoss),
      lifeStagePhrase: profile.lifeStagePhrase,
      skinType: profile.skinType.toLowerCase(),
      ...extra,
    };
    return String(template)
      .replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match))
      .replace(/ {2,}/g, " ")
      .replace(/ ([,.…])/g, "$1")
      .trim();
  }

  /* ── Step graph ────────────────────────────────────────────────── */

  function nextStep(step, answers) {
    switch (step) {
      case "age":
        return "concern";
      case "concern":
        return asksLifeStage(answers) ? "lifeStage" : "skinType";
      case "lifeStage":
        return "skinType";
      case "skinType":
        return "problem";
      case "problem":
        return "stage";
      case "stage":
        return "time";
      case "time":
        return "belief";
      case "belief":
        return "tried";
      case "tried":
        return answers.tried?.includes("nothing") ? "mechanism" : "went";
      case "went":
        return "mechanism";
      case "mechanism":
        return "coffee";
      case "coffee":
        return "coffeeTruth";
      case "coffeeTruth":
        // Botox and fillers only come up for the lines-and-wrinkles avatar.
        return D.CONCERN_TRACK[answers.concern?.[0] ?? ""] === "aging" ? "botox" : "mirror";
      case "botox":
        return "botoxTruth";
      case "botoxTruth":
        return "mirror";
      case "mirror":
        return "goal";
      case "goal":
        return "pledge";
      case "pledge":
        return "analysis";
      case "analysis":
        return "reveal";
      case "reveal":
        return "sameJobs";
      case "sameJobs":
        return "twoFutures";
      case "twoFutures":
        return "offer";
      case "offer":
        return "discount";
      default:
        return "exit";
    }
  }

  /** Only the answers to questions on the path she actually took. */
  function onPath(answers, trail) {
    const visited = new Set(trail);
    return Object.fromEntries(Object.entries(answers).filter(([question]) => visited.has(question)));
  }

  /* ── Blocks (blocks.tsx) ───────────────────────────────────────── */

  /** Keeps hyphenated words like "softer-looking" from breaking at the hyphen. */
  const keepHyphens = (text) =>
    String(text)
      .split(/(\w+(?:-\w+)+)/g)
      .map((part, index) => (index % 2 === 1 ? `<span class="lq-nowrap">${esc(part)}</span>` : esc(part)))
      .join("");

  /** Renders **bold** spans inside a plain string. */
  const rich = (text) =>
    String(text)
      .split(/(\*\*[^*]+\*\*)/g)
      .map((part) =>
        part.startsWith("**") && part.endsWith("**")
          ? `<strong>${keepHyphens(part.slice(2, -2))}</strong>`
          : keepHyphens(part),
      )
      .join("");

  /** Centered body copy. Lines starting with "##" render as the larger pivot line. */
  const paragraphs = (lines) =>
    `<div class="lq-text">${lines
      .map((line) => (line.startsWith("##") ? `<p class="lq-emph">${rich(line.slice(2))}</p>` : `<p>${rich(line)}</p>`))
      .join("")}</div>`;

  const CHECK =
    '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

  const BACK_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M11 18l-6-6 6-6"></path></svg>';

  /* Belief: collagen by age, the visitor's bracket highlighted. */
  function collagenChart(bracket, caption) {
    const baseline = 150;
    const scale = 108 / 100;
    const bars = D.COLLAGEN_BARS.map((bar, index) => {
      const height = bar.value * scale;
      return { ...bar, x: 22 + index * 42, y: baseline - height, height };
    });
    const you = bars[bracket];
    const youCenter = you.x + 15;
    const dropEnd = youCenter - 16;
    return (
      `<div class="lq-card lq-chart"><svg viewBox="0 0 340 172" role="img" aria-label="${esc(
        `Collagen by decade: highest in your 20s, lower every decade and still falling. Your decade, ${you.label}, is highlighted at ${you.value}%.`,
      )}">` +
      `<line x1="20" y1="${baseline}" x2="312" y2="${baseline}" stroke="#DADADA" stroke-width="1"></line>` +
      bars
        .map(
          (bar, index) =>
            `<g><rect class="bar${index === bracket ? " you" : ""}" x="${bar.x}" y="${bar.y}" width="30" height="${bar.height}" rx="3"></rect>` +
            `<text class="xl${index === bracket ? " you" : ""}" x="${bar.x + 15}" y="164" text-anchor="middle">${esc(bar.label)}</text></g>`,
        )
        .join("") +
      `<text class="vlab" x="${bars[0].x + 15}" y="${bars[0].y - 5}" text-anchor="middle">100%</text>` +
      '<path class="arrow" d="M312,124 C322,130 328,140 331,150"></path>' +
      '<path class="arrow" d="M322,146 L332,153 L337,141"></path>' +
      (bracket > 0
        ? `<line class="drop" x1="62" y1="${bars[0].y}" x2="${dropEnd}" y2="${bars[0].y}"></line>` +
          `<g transform="translate(${(62 + dropEnd) / 2 - 26},32)"><rect x="0" y="0" width="52" height="20" rx="10" fill="#B71C1C"></rect>` +
          `<text x="26" y="14" text-anchor="middle" font-size="11.5" font-weight="700" fill="#fff">−${100 - you.value}%</text></g>`
        : "") +
      `<text class="vlab" x="${youCenter}" y="${you.y - 5}" text-anchor="middle">${you.value}%</text>` +
      `<text x="${youCenter}" y="${you.y - 17}" text-anchor="middle" font-size="9" font-weight="700" fill="#B71C1C" letter-spacing=".08em">YOU</text>` +
      `</svg><p class="lq-chart-cap">${rich(caption)}</p></div>`
    );
  }

  /* Belief: collagen after menopause (AAD: ~30% in 5 years, then ~2%/yr). */
  const MENOPAUSE_POINTS = [
    { label: "Start", value: 100 },
    { label: "5 yrs", value: 70 },
    { label: "10 yrs", value: 60 },
    { label: "15 yrs", value: 50 },
    { label: "20 yrs", value: 40 },
  ];

  function menopauseChart(caption) {
    const baseline = 150;
    const points = MENOPAUSE_POINTS.map((point, index) => ({
      ...point,
      x: 40 + index * 68,
      y: baseline - point.value * 1.1,
    }));
    const line = points.map((point, index) => `${index ? "L" : "M"}${point.x},${point.y}`).join(" ");
    const [start, five] = points;
    return (
      '<div class="lq-card lq-chart lq-meno"><svg viewBox="0 0 340 172" role="img" aria-label="Skin collagen after menopause begins: down about 30% in the first 5 years, then about 2% a year.">' +
      '<defs><linearGradient id="lqMenoFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#D32F2F" stop-opacity=".18"></stop><stop offset="1" stop-color="#D32F2F" stop-opacity="0"></stop></linearGradient></defs>' +
      `<line x1="30" y1="${baseline}" x2="320" y2="${baseline}" stroke="#DADADA" stroke-width="1"></line>` +
      `<path d="${line} L${points[4].x},${baseline} L${start.x},${baseline} Z" fill="url(#lqMenoFill)"></path>` +
      `<path class="meno-line" d="${line}"></path>` +
      `<path class="meno-steep" d="M${start.x},${start.y} L${five.x},${five.y}"></path>` +
      points
        .map(
          (point, index) =>
            `<g><circle cx="${point.x}" cy="${point.y}" r="${index < 2 ? 5 : 4}" class="${index < 2 ? "dot steep" : "dot"}"></circle>` +
            `<text class="xl" x="${point.x}" y="164" text-anchor="middle">${esc(point.label)}</text></g>`,
        )
        .join("") +
      `<text class="vlab" x="${start.x}" y="${start.y - 9}" text-anchor="middle">100%</text>` +
      `<g transform="translate(${(start.x + five.x) / 2 + 6},${(start.y + five.y) / 2 - 30})"><rect x="0" y="0" width="60" height="20" rx="10" fill="#B71C1C"></rect>` +
      '<text x="30" y="14" text-anchor="middle" font-size="11.5" font-weight="700" fill="#fff">−30%</text></g>' +
      `<text class="meno-note" x="${points[3].x}" y="${points[3].y - 14}" text-anchor="middle">then ~2% a year</text>` +
      `</svg><p class="lq-chart-cap">${rich(caption)}</p></div>`
    );
  }

  /* Belief: a 4-step loop and a single stat. */
  function cycleDiagram(nodes) {
    // Chevrons sit between the nodes, pointing clockwise around the loop.
    const arrows = [45, 135, 225, 315].map((angle) => {
      const radians = (angle * Math.PI) / 180;
      return { angle, x: 50 + 36 * Math.sin(radians), y: 50 - 36 * Math.cos(radians) };
    });
    return (
      `<div class="lq-card lq-cycle-card"><ol class="lq-cycle" aria-label="${esc(
        `A loop: ${nodes.join(", then ")}, then back to the start.`,
      )}">` +
      '<svg class="lq-cycle-ring" viewBox="0 0 100 100" aria-hidden="true"><circle cx="50" cy="50" r="36"></circle>' +
      arrows
        .map(
          (arrow) =>
            `<path d="M-2.6,-3.2 L1.6,0 L-2.6,3.2" transform="translate(${arrow.x.toFixed(2)},${arrow.y.toFixed(2)}) rotate(${arrow.angle})"></path>`,
        )
        .join("") +
      "</svg>" +
      nodes
        .map(
          (node, index) =>
            `<li class="lq-cycle-node n${index + 1}"><i aria-hidden="true">${index + 1}</i>${esc(node)}</li>`,
        )
        .join("") +
      "</ol></div>"
    );
  }

  const statCard = (big, caption) =>
    `<div class="lq-card lq-stat"><b>${esc(big)}</b><p>${rich(caption)}</p></div>`;

  /* Coffee truth: the cup at a glance. */
  const coffeeFacts = (facts) =>
    `<div class="lq-facts">${facts
      .map(([big, small]) => `<div class="lq-fact"><b>${esc(big)}</b><span>${esc(small)}</span></div>`)
      .join("")}</div>`;

  /* Mechanism figures. */
  const tag = (x, y, text, color, big = false, anchor = "middle") =>
    `<text class="lq-tag${big ? " big" : ""}" x="${x}" y="${y}" fill="${color}" text-anchor="${anchor}">${esc(text)}</text>`;

  const leadLine = (tx, ty, x2, y2, color) =>
    `<line class="lq-lead" x1="${tx}" y1="${ty}" x2="${x2}" y2="${y2}" stroke="${color}"></line><circle cx="${x2}" cy="${y2}" r="6" fill="${color}"></circle>`;

  const leakyBucket = (labels) =>
    '<div class="lq-figure">' +
    image({
      src: "/assets/quiz/mech-leaky-bucket.webp",
      alt: "Cartoon: a scoop pours collagen characters into a lilac bucket full of holes while little critters snip new holes and the collagen tumbles back out",
      width: 1000,
      height: 671,
    }) +
    '<svg class="lq-overlay" viewBox="0 0 1000 671" preserveAspectRatio="none" aria-hidden="true">' +
    leadLine(300, 118, 445, 190, "#9A4FA3") +
    tag(225, 96, labels.pour, "#9A4FA3") +
    tag(500, 392, labels.skin, "#6E4AA8", true) +
    leadLine(170, 448, 318, 530, "#3A3A3A") +
    tag(150, 428, labels.breakdown, "#3A3A3A") +
    leadLine(850, 500, 712, 575, "#B71C1C") +
    tag(985, 482, labels.leak, "#B71C1C", false, "end") +
    "</svg></div>";

  function fromWithin(labels) {
    const downArrow = "M0,0 L0,40 M-13,27 L0,40 L13,27";
    const upArrow = "M0,40 L0,0 M-13,13 L0,0 L13,13";
    return (
      '<div class="lq-card lq-fw"><div class="lq-figure">' +
      image({
        src: "/assets/quiz/mech-from-within.webp",
        alt: "Two blocks of skin side by side: on the left a cream sits on top and the collagen below stays thin and broken; on the right violet nutrients rise from the blood vessels and the collagen looks full",
        width: 1000,
        height: 671,
      }) +
      '<svg class="lq-overlay" viewBox="0 0 1000 671" preserveAspectRatio="none" aria-hidden="true">' +
      '<line class="shieldLineU" x1="150" y1="160" x2="390" y2="160"></line>' +
      '<line class="shieldLine" x1="150" y1="160" x2="390" y2="160"></line>' +
      ["", " a2", " a3"]
        .map(
          (delay) =>
            `<g transform="translate(270,74)"><path class="arrLu${delay}" d="${downArrow}"></path><path class="arrL${delay}" d="${downArrow}"></path></g>`,
        )
        .join("") +
      ["", " a2", " a3"]
        .map((delay) => `<ellipse class="rip${delay}" cx="270" cy="160" rx="44" ry="10"></ellipse>`)
        .join("") +
      '<line class="zlineU" x1="70" y1="566" x2="470" y2="566"></line>' +
      '<line class="zlineOff" x1="70" y1="566" x2="470" y2="566"></line>' +
      `<text class="zl off" x="270" y="612" text-anchor="middle">${esc(labels.zone)}</text>` +
      ["", " b2", " b3"]
        .map(
          (delay) =>
            `<g transform="translate(668,440)"><path class="arrRu${delay}" d="${upArrow}"></path><path class="arrR${delay}" d="${upArrow}"></path></g>`,
        )
        .join("") +
      '<line class="zlineU" x1="530" y1="566" x2="930" y2="566"></line>' +
      '<line class="zline" x1="530" y1="566" x2="930" y2="566"></line>' +
      `<text class="zl on" x="730" y="612" text-anchor="middle">${esc(labels.zone)}</text>` +
      "</svg></div>" +
      `<div class="lq-caps"><div class="l">${esc(labels.left)}</div><div class="r">${esc(labels.right)}</div></div></div>`
    );
  }

  const PHOTOS = {
    oneCup: {
      src: "/assets/quiz/mech-one-cup.webp",
      alt: "Left: a tired woman over a counter crowded with skincare jars and collagen tubs. Right: the same woman smiling in a sunny kitchen with one purple ube coffee",
    },
    momCup: {
      src: "/assets/quiz/mech-mom-cup.webp",
      alt: "Left: a tired mom at a messy kitchen counter with baby bottles and a mug of black coffee. Right: the same mom smiling in a tidy, sunny kitchen with a purple ube coffee",
    },
    dryBarrier: {
      src: "/assets/quiz/mech-dry-barrier.webp",
      alt: "Two blocks of skin: on the left the surface is cracked and water escapes; on the right the skin looks plump and holds water, with violet particles among full collagen",
    },
    rednessCalm: {
      src: "/assets/quiz/mech-redness-calm.webp",
      alt: "Two blocks of skin: on the left the surface is red with widened vessels close to the top; on the right the skin is calm and even, with violet particles among full collagen",
    },
  };

  /** The mechanism visual for a track screen, with its labels where it has them. */
  function mechanismFigure(name, labels = {}) {
    if (name === "leakyBucket") {
      return leakyBucket({
        pour: labels.pour ?? "",
        skin: labels.skin ?? "",
        breakdown: labels.breakdown ?? "",
        leak: labels.leak ?? "",
      });
    }
    if (name === "fromWithin") {
      return fromWithin({ left: labels.left ?? "", right: labels.right ?? "", zone: labels.zone ?? "" });
    }
    const photo = PHOTOS[name];
    const figure = `<div class="lq-figure">${image({ src: photo.src, alt: photo.alt, width: 1000, height: 671 })}</div>`;
    if (!labels.left || !labels.right) return figure;
    return `<div class="lq-card">${figure}<div class="lq-caps"><div class="l">${esc(labels.left)}</div><div class="r">${esc(labels.right)}</div></div></div>`;
  }

  /* Reviews and loaders. */
  const reviews = (items) =>
    `<div class="lq-reviews">${items
      .map((review) => {
        const parts = review.quote.split("\n\n");
        const quote = parts
          .map(
            (part, index) =>
              `<span class="lq-quote-part">${index === 0 ? "“" : ""}${esc(part)}${index === parts.length - 1 ? "”" : ""}</span>`,
          )
          .join("");
        return (
          '<figure class="lq-review"><div class="lq-stars" role="img" aria-label="5 out of 5 stars">★★★★★</div>' +
          `<blockquote class="lq-quote">${quote}</blockquote>` +
          `<figcaption><div class="lq-name">${esc(review.name)}</div><div class="lq-verified">✓ Lavendr customer</div></figcaption></figure>`
        );
      })
      .join("")}</div>`;

  function loaderStepState(steps, starts, index, elapsed) {
    const startMs = starts[index];
    const durationMs = steps[index].seconds * 1000;
    const done = elapsed >= startMs + durationMs;
    const active = !done && elapsed >= startMs;
    const pct = active ? Math.floor(((elapsed - startMs) / durationMs) * 100) : 0;
    return { state: done ? "is-done" : active ? "is-active" : "is-pending", pct };
  }

  const loaderStepInner = (step, state, pct) =>
    `<div class="lq-lstep-row"><span class="lq-lstep-label">${esc(step.text)}</span>` +
    (state === "is-done" ? `<span class="lq-check">${CHECK}</span>` : "") +
    (state === "is-active" ? `<span class="lq-pct" aria-hidden="true">${pct}%</span>` : "") +
    "</div>" +
    (state === "is-active" ? `<div class="lq-bar" aria-hidden="true"><div style="width:${pct}%"></div></div>` : "");

  const loaderStarts = (steps) =>
    steps.map((_, index) => steps.slice(0, index).reduce((sum, step) => sum + step.seconds * 1000, 0));

  function analysisLoader(steps) {
    const starts = loaderStarts(steps);
    return (
      `<div class="lq-loader-steps" data-lq-analysis><p class="lq-sr-only" role="status">${esc(steps[0]?.text ?? "")}</p>` +
      steps
        .map((step, index) => {
          const { state, pct } = loaderStepState(steps, starts, index, 0);
          return `<div class="lq-lstep ${state}">${loaderStepInner(step, state, pct)}</div>`;
        })
        .join("") +
      "</div>"
    );
  }

  function patchAnalysis(container, steps, elapsed) {
    const starts = loaderStarts(steps);
    // Announce only the active line, not every percentage tick.
    const activeIndex = starts.findIndex(
      (start, index) => elapsed >= start && elapsed < start + steps[index].seconds * 1000,
    );
    const status = container.querySelector(".lq-sr-only");
    const statusText = activeIndex >= 0 ? steps[activeIndex].text : "";
    if (status && status.textContent !== statusText) status.textContent = statusText;
    container.querySelectorAll(".lq-lstep").forEach((el, index) => {
      const { state, pct } = loaderStepState(steps, starts, index, elapsed);
      const className = `lq-lstep ${state}`;
      if (el.className !== className) {
        el.className = className;
        el.innerHTML = loaderStepInner(steps[index], state, pct);
      } else if (state === "is-active") {
        const pctEl = el.querySelector(".lq-pct");
        const fill = el.querySelector(".lq-bar > div");
        if (pctEl) pctEl.textContent = `${pct}%`;
        if (fill) fill.style.width = `${pct}%`;
      }
    });
  }

  const progressLoader = (label) =>
    `<div class="lq-bigbar" data-lq-progress><div class="lq-bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-label="${esc(label)}"><div style="width:0%"></div></div>` +
    `<p>${esc(label)} <b aria-hidden="true">0%</b></p></div>`;

  function patchProgress(container, totalMs, elapsed) {
    const pct = Math.floor((elapsed / totalMs) * 100);
    const bar = container.querySelector(".lq-bar");
    bar.setAttribute("aria-valuenow", String(pct));
    bar.firstElementChild.style.width = `${pct}%`;
    container.querySelector("p b").textContent = `${pct}%`;
  }

  /* Skin type reveal. */
  function skinReveal(copy, revealImage, profile, explain) {
    const rows = [
      { icon: "🔍", tint: "#F6EEF8", label: copy.rows.patternLabel, value: copy.rows.patternValue, tone: "hl" },
      {
        icon: "📏",
        tint: "#F1ECFA",
        label: copy.rows.depthLabel,
        value: profile.stage.severe ? copy.rows.depthSevere : copy.rows.depthMild,
        tone: "hl",
      },
      { icon: "📍", tint: "#F6EEF8", label: COPY.reveal.rows.where, value: profile.problemAll },
      { icon: "💧", tint: "#F1ECFA", label: COPY.reveal.rows.skinType, value: profile.skinType },
      { icon: "🧴", tint: "#F6EEF8", label: COPY.reveal.rows.tried, value: profile.tried },
      { icon: "🌱", tint: "#E3F5E9", label: copy.rows.potentialLabel, value: copy.rows.potentialValue, tone: "good" },
    ];
    return (
      '<div class="lq-rv"><div class="lq-rv-card">' +
      `<div class="lq-rv-hdr"><h1 class="lq-rv-title">${esc(copy.title)}</h1><div class="lq-rv-pill">${esc(copy.pill)}</div></div>` +
      `<div class="lq-rv-img">${image({ src: revealImage.src, alt: revealImage.alt, width: 520, height: 520 })}</div>` +
      `<div class="lq-rv-name"><b>${esc(copy.name)}</b><span>${esc(copy.sub)}</span></div>` +
      `<div class="lq-rv-slider"><div class="lq-rv-track"><div class="lq-rv-thumb" style="--lq-end:${profile.stage.position}%"><div class="lq-rv-tip">${esc(COPY.reveal.here)}</div></div></div></div>` +
      `<div class="lq-rv-labels">${copy.stageLabels.map((stage) => `<span>${esc(stage)}</span>`).join("")}</div>` +
      '<div class="lq-rv-alert"><div class="lq-rv-alert-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path></svg></div>' +
      `<div><b>What ${esc(copy.pill.toLowerCase())} means</b>${explain.map((line) => `<p>${esc(line)}</p>`).join("")}</div></div>` +
      "</div>" +
      `<div class="lq-rv-stats">${rows
        .map(
          (row) =>
            `<div class="lq-rv-stat"><div class="lq-rv-stat-icon" style="background:${row.tint}" aria-hidden="true">${row.icon}</div>` +
            `<div><small>${esc(row.label)}</small><b${row.tone ? ` class="${row.tone}"` : ""}>${esc(row.value)}</b></div></div>`,
        )
        .join("")}</div></div>`
    );
  }

  /* Same jobs comparison. The "goal" face is one stage milder, never more. */
  const MILDER = { "stage-4": "stage-3", "stage-3": "stage-2", "stage-2": "stage-1" };

  function sameJobs(stage, photo, rows, cardBody) {
    const copy = COPY.sameJobs;
    // Stage 1 has no milder photo, so it compares against where the same
    // routine leads instead.
    const isMildest = !stage || !(stage in MILDER);
    const left = isMildest ? "stage-2" : stage;
    const right = isMildest ? "stage-1" : MILDER[stage];
    return (
      '<div class="lq-cp"><div class="lq-cp-top">' +
      `<div class="lq-cp-img lq-cp-l"><figure>${image({ src: photo(left), width: 480, height: 480 })}<figcaption>${esc(
        isMildest ? copy.imageLeftStage1 : copy.imageLeft,
      )}</figcaption></figure></div>` +
      `<div class="lq-cp-img lq-cp-r"><figure>${image({ src: photo(right), width: 480, height: 480 })}<figcaption>${esc(
        copy.imageRight,
      )}</figcaption></figure></div></div>` +
      `<div class="lq-cp-labels"><div class="lq-cp-label lq-cp-l">${esc(copy.headLeft)}</div><div class="lq-cp-label lq-cp-r">${esc(copy.headRight)}</div></div>` +
      rows
        .map(
          ([l, r]) =>
            `<div class="lq-cp-row"><div class="lq-cp-cell lq-cp-l"><span class="lq-cp-mark" aria-hidden="true">✕</span><span>${esc(l)}</span></div>` +
            `<div class="lq-cp-cell lq-cp-r"><span class="lq-cp-mark" aria-hidden="true">✓</span><span>${esc(r)}</span></div></div>`,
        )
        .join("") +
      `<div class="lq-cp-card"><b>${esc(copy.cardTitle)}</b>${cardBody.map((line) => `<p>${rich(line)}</p>`).join("")}</div></div>`
    );
  }

  /* Two futures. */
  function twoFuturesChart({ yTop, yLow, milestones, close, stats, statsCaption }) {
    const copy = COPY.twoFutures;
    return (
      '<div class="lq-fp"><div class="lq-fp-card">' +
      `<div class="lq-fp-legend"><span><i class="with"></i>${esc(copy.legendWith)}</span><span><i class="without"></i>${esc(copy.legendWithout)}</span></div>` +
      '<svg viewBox="0 0 340 200" role="img" aria-label="Two lines from today: with your plan rising through week 1, weeks 2 to 4 and weeks 4 to 8; same routine slowly declining">' +
      '<defs><linearGradient id="lqFpWith" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#6E4AA8" stop-opacity=".24"></stop><stop offset="1" stop-color="#6E4AA8" stop-opacity="0"></stop></linearGradient></defs>' +
      '<line x1="56" y1="46" x2="326" y2="46" class="grid"></line>' +
      '<line x1="56" y1="106" x2="326" y2="106" class="grid"></line>' +
      '<line x1="56" y1="158" x2="326" y2="158" class="grid"></line>' +
      `<text x="4" y="50" class="y">${esc(yTop)}</text>` +
      `<text x="4" y="110" class="y">${esc(copy.yMid)}</text>` +
      `<text x="4" y="162" class="y">${esc(yLow)}</text>` +
      '<path d="M60,106 C90,102 110,96 140,86 S220,58 326,44 L326,106 Z" fill="url(#lqFpWith)"></path>' +
      '<path class="without" d="M60,106 C130,110 220,120 326,132"></path>' +
      '<path class="with" d="M60,106 C90,102 110,96 140,86 S220,58 326,44"></path>' +
      '<circle cx="60" cy="106" r="5" fill="#151515"></circle>' +
      '<circle cx="140" cy="86" r="4.5" class="dot"></circle>' +
      '<circle cx="230" cy="60" r="4.5" class="dot"></circle>' +
      '<circle cx="326" cy="44" r="5.5" class="dot"></circle>' +
      '<circle cx="326" cy="132" r="4.5" fill="#C273CB" stroke="#fff" stroke-width="2"></circle>' +
      copy.xLabels
        .map(
          (label, index) =>
            `<text x="${[60, 140, 230, 322][index]}" y="184" class="x" text-anchor="${index === 3 ? "end" : "middle"}">${esc(label)}</text>`,
        )
        .join("") +
      "</svg>" +
      `<div class="lq-fp-miles">${milestones
        .map(([when, line]) => `<div class="lq-fp-m"><b>${esc(when)}</b><span>${esc(line)}</span></div>`)
        .join("")}</div></div>` +
      `<div class="lq-fp-close">${close.map((line) => `<p>${rich(line)}</p>`).join("")}</div>` +
      `<div class="lq-fp-stats">${stats.map(([value, line]) => `<div><b>${esc(value)}</b><span>${esc(line)}</span></div>`).join("")}</div>` +
      `<p class="lq-fp-caption">${esc(statsCaption)}</p></div>`
    );
  }

  /* Offer. */
  const CHECK_MARK = '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  const BADGE =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 12l2 2 4-4"></path><path d="M12 3l1.45 1.32a1 1 0 0 0 .87.28l1.93-.29a1 1 0 0 1 1.11.75l.42 1.89a1 1 0 0 0 .58.67l1.74.78a1 1 0 0 1 .52 1.25l-.72 1.82a1 1 0 0 0 .06.92l1.04 1.64a1 1 0 0 1-.28 1.32l-1.52 1.16a1 1 0 0 0-.38.82l.05 1.97a1 1 0 0 1-.9 1.01l-1.95.19a1 1 0 0 0-.78.49l-.96 1.7a1 1 0 0 1-1.27.46l-1.78-.82a1 1 0 0 0-.86 0l-1.78.82a1 1 0 0 1-1.27-.46l-.96-1.7a1 1 0 0 0-.78-.49l-1.95-.19a1 1 0 0 1-.9-1.01l.05-1.97a1 1 0 0 0-.38-.82L2.3 15.4a1 1 0 0 1-.28-1.32l1.04-1.64a1 1 0 0 0 .06-.92L2.4 9.7a1 1 0 0 1 .52-1.25l1.74-.78a1 1 0 0 0 .58-.67l.42-1.89a1 1 0 0 1 1.11-.75l1.93.29a1 1 0 0 0 .87-.28L12 3z"></path></svg>';
  const GIFT =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1"></rect><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"></path></svg>';

  function offerCard(offer, band, special) {
    const copy = { ...COPY.offer, ...offer };
    const callouts = (items) =>
      items.map(([big, small]) => `<div class="lq-of-call"><b>${esc(big)}</b><small>${esc(small)}</small></div>`).join("");
    return (
      `<div class="lq-of"><div class="lq-of-badge"><span>${esc(copy.badge)}</span></div>` +
      `<h1 class="lq-of-headline">${esc(copy.headlineA)} <span>${esc(copy.headlineB)}</span></h1>` +
      `<p class="lq-of-desc">${rich(copy.desc)}</p>` +
      `<div class="lq-of-social"><i role="img" aria-label="4.9 out of 5 stars">★★★★★</i><span>${esc(copy.rating)}</span></div>` +
      '<div class="lq-of-hero"><div class="lq-of-hero-grid">' +
      `<div class="lq-of-col l">${callouts(copy.callouts.left)}</div>` +
      image({
        src: "/assets/quiz/offer-hero.webp",
        alt: "Lavendr skincare coffee canister next to a glass of iced ube coffee",
        width: 640,
        height: 640,
      }) +
      `<div class="lq-of-col r">${callouts(copy.callouts.right)}</div>` +
      `</div><div class="lq-of-band">${esc(band)}</div></div>` +
      `<div class="lq-of-benefits">${copy.benefits
        .map((benefit) => `<div class="lq-of-benefit"><i>${CHECK_MARK}</i>${esc(benefit)}</div>`)
        .join("")}</div>` +
      `<div class="lq-of-special"><i>${GIFT}</i><div class="lq-of-special-text">${special}</div></div>` +
      `<div class="lq-of-trust">${copy.trust.map((item) => `<span>${BADGE}${esc(item)}</span>`).join("")}</div>` +
      `<p class="lq-of-guarantee">${esc(copy.guarantee)}</p></div>`
    );
  }

  /* ── State ─────────────────────────────────────────────────────── */

  const state = {
    trail: ["age"],
    answers: {},
    drafts: {},
    today: formatToday(),
    lock: false,
    settling: false,
    startedAt: 0,
    trailLength: 1,
  };
  let settleTimer = 0;
  let stopTimeline = null;

  /** Everything a screen derives from state, like QuizFunnel's render. */
  function view() {
    const step = state.trail[state.trail.length - 1];
    const position = state.trail.length;
    // Multi-select picks before Continue; going back restores the saved answer.
    const draft = QUESTION_STEPS.has(step) ? (state.drafts[step] ?? state.answers[step] ?? []) : [];
    const profile = buildProfile(onPath(state.answers, state.trail));
    const extra = { savings: SAVINGS, today: state.today, perCup: PER_CUP };
    return {
      step,
      position,
      draft,
      profile,
      track: profile.track,
      copy: profile.track.copy,
      text: (template) => fill(template, profile, extra),
      // The mechanism screen logs which of its three versions she saw.
      screen: step === "mechanism" ? `mechanism-${profile.mechanism}` : step,
    };
  }

  const isSelected = (v, id) =>
    MULTI[v.step] ? v.draft.includes(id) : state.answers[v.step]?.[0] === id;

  function listOptions(v, question) {
    return (
      `<div class="lq-options${MULTI[question] ? " lq-multi" : ""}" role="group">` +
      optionsFor(question, state.answers)
        .map((option) => {
          const selected = isSelected(v, option.id);
          return (
            `<button type="button" aria-pressed="${selected}" class="lq-option${selected ? " is-selected" : ""}" data-lq-option="${esc(option.id)}">` +
            (option.emoji ? `<span class="lq-option-emoji" aria-hidden="true">${esc(option.emoji)}</span>` : "") +
            `<span>${esc(option.label)}</span></button>`
          );
        })
        .join("") +
      "</div>"
    );
  }

  function gridOptions(v, question, className) {
    return (
      `<div class="lq-grid ${className}" role="group">` +
      optionsFor(question, state.answers)
        .map((option, index) => {
          const selected = isSelected(v, option.id);
          return (
            `<button type="button" aria-pressed="${selected}" class="lq-tile${selected ? " is-selected" : ""}" data-lq-option="${esc(option.id)}">` +
            image({ src: option.image ?? "", width: 480, height: 480, loading: index < 4 ? "eager" : "lazy" }) +
            `<span class="lq-tile-cap"><b>${esc(option.label)}</b>${option.sub ? `<small>${esc(option.sub)}</small>` : ""}</span></button>`
          );
        })
        .join("") +
      "</div>"
    );
  }

  function beliefVisual(v, belief) {
    switch (belief.visual) {
      case "collagenDecade":
        return collagenChart(v.profile.bracket, v.text(COPY.collagenCaption));
      case "menopauseDrop":
        return menopauseChart(v.text(belief.statCaption ?? ""));
      case "cycle":
        return cycleDiagram(belief.cycleNodes ?? []);
      case "stat":
        return statCard(belief.statBig ?? "", v.text(belief.statCaption ?? ""));
      default:
        return "";
    }
  }

  const h1 = (text, className = "lq-title") => `<h1 class="${className}">${esc(text)}</h1>`;
  const helper = (text) => `<p class="lq-helper">${esc(text)}</p>`;

  /** The screen's markup, its sticky button and whether it uses the wide column. */
  function buildStep(v) {
    const { step, profile, copy, text } = v;
    let body = "";
    // Which handler the sticky button runs.
    let cta = null;
    let wide = false;

    switch (step) {
      case "age":
        body =
          `<p class="lq-eyebrow">${esc(D.SALE_EYEBROW)}</p>` +
          h1(text(COPY.age.headline), "lq-headline") +
          `<p class="lq-gift">${esc(COPY.age.gift)}</p>` +
          `<h2 class="lq-title lq-small">${esc(COPY.age.question)}</h2>` +
          listOptions(v, "age");
        break;
      case "concern":
        body = h1(COPY.concern.title) + listOptions(v, "concern");
        break;
      case "lifeStage":
        body = h1(COPY.lifeStage.title) + helper(COPY.lifeStage.helper) + listOptions(v, "lifeStage");
        break;
      case "skinType":
        wide = true;
        body = h1(COPY.skinType.title) + helper(COPY.skinType.helper) + gridOptions(v, "skinType", "lq-stage-grid lq-skin-grid");
        break;
      case "problem":
        body = h1(copy.areas.title) + helper(copy.areas.helper) + gridOptions(v, "problem", "lq-areas");
        cta = { label: "Continue", action: "multi", disabled: v.draft.length === 0 };
        break;
      case "stage":
        wide = true;
        body = h1(copy.stage.title) + helper(copy.stage.helper) + gridOptions(v, "stage", "lq-stage-grid");
        break;
      case "time":
        body = h1(COPY.time.title) + listOptions(v, "time");
        break;
      case "belief": {
        const belief =
          profile.lifeStage === "peri" || profile.lifeStage === "meno"
            ? copy.belief.menopause
            : profile.lifeStage === "newMom"
              ? copy.belief.newMom
              : copy.belief.default;
        body = h1(text(belief.title)) + beliefVisual(v, belief) + paragraphs(belief.body.map(text));
        cta = { label: "Continue", action: "info" };
        break;
      }
      case "tried":
        body = h1(copy.tried.title) + helper(copy.tried.helper) + listOptions(v, "tried");
        cta = { label: "Continue", action: "multi", disabled: v.draft.length === 0 };
        break;
      case "went":
        body = h1(COPY.went.title) + listOptions(v, "went");
        break;
      case "mechanism": {
        const mechanism = copy.mechanism[profile.mechanism];
        let title = mechanism.title;
        let lines = mechanism.body;
        let labels = mechanism.labels;
        if (profile.mechanism === "topical" && !profile.triedTopical) {
          // Tried only non-topical things (devices, more water, cutting triggers…).
          title = mechanism.titleLifestyle ?? title;
          if (mechanism.bodyLifestyleLead) lines = [mechanism.bodyLifestyleLead, ...lines.slice(1)];
          if (mechanism.labelsLifestyleLeft) labels = { ...labels, left: mechanism.labelsLifestyleLeft };
        } else if (profile.mechanism === "collagen") {
          lines = lines.map((line, index) =>
            index === 0 && profile.under40 && mechanism.leadYoung
              ? mechanism.leadYoung
              : index === 1 && profile.wentPositive && mechanism.wentPositive
                ? mechanism.wentPositive
                : line,
          );
        } else if (profile.mechanism === "nothing" && profile.under50 && mechanism.titleYoung) {
          title = mechanism.titleYoung;
        }
        body = h1(text(title)) + mechanismFigure(mechanism.image, labels) + paragraphs(lines.map(text));
        cta = { label: "Continue", action: "info" };
        break;
      }
      case "coffee":
        body = h1(COPY.coffee.title) + listOptions(v, "coffee");
        break;
      case "coffeeTruth": {
        // No "keep your morning cup" for someone who doesn't drink coffee.
        const noCoffee = profile.coffee === "none";
        body =
          h1(text(noCoffee ? copy.coffeeTruth.titleNone : copy.coffeeTruth.title)) +
          `<p class="lq-lede">${rich(text(copy.coffeeTruth.byAnswer[profile.coffee]))}</p>` +
          (profile.lifeStage === "newMom" && !noCoffee
            ? mechanismFigure("momCup")
            : coffeeFacts(copy.coffeeFacts ?? COPY.coffeeFacts)) +
          paragraphs((noCoffee ? copy.coffeeTruth.bodyNone : copy.coffeeTruth.body).map(text));
        cta = { label: "Continue", action: "info" };
        break;
      }
      case "botox":
        body = h1(COPY.botoxQ.title) + helper(COPY.botoxQ.helper) + listOptions(v, "botox");
        break;
      case "botoxTruth":
        body = h1(COPY.botoxTruth.title) + paragraphs(COPY.botoxTruth.body);
        cta = { label: "Continue", action: "info" };
        break;
      case "mirror":
        body = h1(copy.mirror.title) + listOptions(v, "mirror");
        break;
      case "goal":
        body = h1(text(copy.goalTitle)) + listOptions(v, "goal");
        break;
      case "pledge":
        body =
          h1(COPY.pledge.title) +
          helper(profile.coffee === "none" ? COPY.pledge.helperNoCoffee : COPY.pledge.helper) +
          listOptions(v, "pledge");
        break;
      case "analysis":
        body =
          h1(COPY.analysis.title) +
          analysisLoader(analysisSteps(v)) +
          reviews(copy.reviews.analysis);
        break;
      case "reveal":
        body = skinReveal(
          copy.reveal,
          v.track.revealImage,
          profile,
          (profile.stage.severe ? copy.reveal.explain.severe : copy.reveal.explain.mild).map(text),
        );
        cta = { label: "Continue", action: "info" };
        break;
      case "sameJobs":
        body =
          h1(copy.sameJobs.title) +
          sameJobs(
            state.answers.stage?.[0],
            v.track.stagePhoto,
            copy.sameJobs.rows,
            COPY.sameJobs.cardBody.map((line, index) =>
              index === 1 && profile.coffee === "none" ? COPY.sameJobs.cardBodyNoCoffee : line,
            ),
          );
        cta = { label: "Continue", action: "info" };
        break;
      case "twoFutures":
        body =
          '<div class="lq-pill-wrap"><span class="lq-pill"><i><svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg></i>' +
          `${esc(text(COPY.twoFutures.pill))}</span></div>` +
          `<p class="lq-eyebrow lq-small">${esc(COPY.twoFutures.eyebrow)}</p>` +
          h1(text(copy.twoFutures.title)) +
          paragraphs([COPY.twoFutures.intro, copy.twoFutures.intro2].map(text)) +
          twoFuturesChart({
            yTop: copy.twoFutures.yTop,
            yLow: copy.twoFutures.yLow,
            milestones: copy.twoFutures.milestones.map(([when, line]) => [when, text(line)]),
            close: copy.twoFutures.close.map(text),
            stats: copy.twoFutures.stats,
            statsCaption: COPY.twoFutures.statsCaption,
          });
        cta = { label: COPY.twoFutures.button, action: "info" };
        break;
      case "offer":
        body = offerCard(
          copy.offer,
          text(COPY.offer.band),
          COPY.offer.special
            .map(
              (line, index) =>
                `<p>${index === 0 ? `<em>${esc(COPY.offer.specialLabel)} </em>` : ""}${rich(text(line))}</p>`,
            )
            .join(""),
        );
        cta = { label: COPY.offer.button, action: "info" };
        break;
      case "discount":
        body =
          h1(text(COPY.discount.title)) +
          `<p class="lq-sub">${esc(COPY.discount.sub)}</p>` +
          progressLoader(text(COPY.discount.bar)) +
          reviews(copy.reviews.discount);
        break;
    }
    return { body, cta, wide };
  }

  function analysisSteps(v) {
    return v.copy.analysis.map((line, index) => ({
      text: index === 2 && v.profile.triedNothing ? v.text(v.copy.analysisNothingTried) : v.text(line),
      seconds: COPY.analysis.seconds[index] ?? 3,
    }));
  }

  /* ── Rendering ─────────────────────────────────────────────────── */

  function updateProgress(v) {
    // Shorter paths finish the bar on their own last screen: no Botox pair off
    // the aging track, no life-stage question from 60 up, no "went" screen
    // when she tried nothing.
    const concernId = state.answers.concern?.[0];
    const pathLength =
      LONGEST_PATH -
      (concernId && D.CONCERN_TRACK[concernId] !== "aging" ? 2 : 0) -
      (state.answers.age && !asksLifeStage(state.answers) ? 1 : 0) -
      (v.profile.triedNothing ? 1 : 0);
    const progress =
      v.step === "discount" ? 100 : Math.min(100, Math.round(((v.position - 1) / (pathLength - 1)) * 100));
    const bar = root.querySelector(".lq-progress-track");
    if (!bar) return;
    bar.setAttribute("aria-valuenow", String(progress));
    const fill = bar.querySelector(".lq-progress-fill");
    if (fill) fill.style.width = `${progress}%`;
  }

  function renderChrome(v) {
    const mast = root.querySelector(".lq-mast");
    const back = mast.querySelector(".lq-back");
    if (v.position > 1 && !back) {
      mast.insertAdjacentHTML(
        "afterbegin",
        `<button type="button" class="lq-back" aria-label="Back" data-lq-back>${BACK_ICON}</button>`,
      );
    } else if (v.position <= 1 && back) {
      back.remove();
    }
    const header = root.querySelector(".lq-header");
    const promo = header.querySelector(".lq-promo");
    if (v.step === "age" && !promo) {
      header
        .querySelector(".lq-progress")
        .insertAdjacentHTML("beforebegin", `<div class="lq-promo">${esc(v.text(COPY.promoBar))}</div>`);
    } else if (v.step !== "age" && promo) {
      promo.remove();
    }
    updateProgress(v);
  }

  /** The sticky button. Like React, it stays mounted between two screens that both have one. */
  function renderCta(cta) {
    let bar = root.querySelector(".lq-cta-bar");
    if (!cta) {
      bar?.remove();
      return;
    }
    if (!bar) {
      root.insertAdjacentHTML(
        "beforeend",
        '<div class="lq-cta-bar"><button type="button" class="lq-cta" data-lq-cta></button></div>',
      );
      bar = root.querySelector(".lq-cta-bar");
    }
    const button = bar.querySelector(".lq-cta");
    if (button.textContent !== cta.label) button.textContent = cta.label;
    button.disabled = Boolean(cta.disabled);
    button.setAttribute("data-lq-cta", cta.action);
  }

  function startTimeline(totalMs, onTick) {
    const start = performance.now();
    let finished = false;
    let doneTimer = 0;
    const id = window.setInterval(() => {
      const now = Math.min(performance.now() - start, totalMs);
      onTick(now);
      if (now >= totalMs && !finished) {
        finished = true;
        window.clearInterval(id);
        doneTimer = window.setTimeout(() => goNext(state.answers), 450);
      }
    }, 60);
    // Leaving the screen (e.g. Back) cancels a pending auto-advance too.
    stopTimeline = () => {
      window.clearInterval(id);
      window.clearTimeout(doneTimer);
    };
  }

  function startLoaders(v, section) {
    if (v.step === "analysis") {
      const steps = analysisSteps(v);
      const container = section.querySelector("[data-lq-analysis]");
      const totalMs = steps.reduce((sum, step) => sum + step.seconds * 1000, 0);
      startTimeline(totalMs, (elapsed) => patchAnalysis(container, steps, elapsed));
    } else if (v.step === "discount") {
      const container = section.querySelector("[data-lq-progress]");
      const totalMs = COPY.discount.seconds * 1000;
      startTimeline(totalMs, (elapsed) => patchProgress(container, totalMs, elapsed));
    }
  }

  /** What QuizFunnel's [screen, position] effect does after every screen change. */
  function onScreen(v, section) {
    state.trailLength = v.position;
    state.lock = false;
    // Ignore taps for a moment after a new screen appears: the sticky button
    // and the next screen's options sit under the same finger, so a double
    // tap would otherwise skip a screen.
    state.settling = true;
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(() => {
      state.settling = false;
    }, 400);
    window.scrollTo({ top: 0 });
    // Move focus to the new question so keyboard and screen-reader users
    // land on it instead of the page body.
    if (v.position > 1) {
      const heading = section.querySelector("h1");
      if (heading) {
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
      }
    }
    track("quiz_step", { quizId: QUIZ_ID, step: v.position, screen: v.screen });
  }

  /** Mounts the current screen (a fresh section, so its entry animation replays). */
  function renderStep() {
    stopTimeline?.();
    stopTimeline = null;
    const v = view();
    const { body, cta, wide } = buildStep(v);
    const section = document.createElement("section");
    section.className = `lq-step${wide ? " lq-wide" : ""}`;
    section.innerHTML = body;
    const main = root.querySelector(".lq-main");
    const old = main.querySelector(".lq-step");
    if (old) old.replaceWith(section);
    else main.appendChild(section);
    renderChrome(v);
    renderCta(cta);
    startLoaders(v, section);
    onScreen(v, section);
  }

  /** Re-renders what an answer changes on the same screen (React's in-place update). */
  function updateSelection() {
    const v = view();
    root.querySelectorAll(".lq-step [data-lq-option]").forEach((button) => {
      const selected = isSelected(v, button.getAttribute("data-lq-option"));
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-pressed", String(selected));
    });
    const cta = root.querySelector('.lq-cta[data-lq-cta="multi"]');
    if (cta) cta.disabled = v.draft.length === 0;
    updateProgress(v);
  }

  /* ── Navigation and answers ────────────────────────────────────── */

  function goNext(nextAnswers) {
    const step = state.trail[state.trail.length - 1];
    const next = nextStep(step, nextAnswers);
    if (next === "exit") {
      // Only answers on the path actually taken: going back and switching
      // branch can leave a stale answer (e.g. "went") in state.
      const finalAnswers = onPath(nextAnswers, state.trail);
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ quizId: QUIZ_ID, answers: finalAnswers, completedAt: new Date().toISOString() }),
        );
      } catch {
        // Storage is optional.
      }
      // Every answer in one event so each completer's full profile is one
      // row. The pledge answer is already logged on its own quiz_answer event.
      const answerFields = Object.fromEntries(
        D.QUESTIONS.filter((question) => question !== "pledge").map((question) => [
          question,
          finalAnswers[question]?.join(",") || null,
        ]),
      );
      track("quiz_complete", {
        quizId: QUIZ_ID,
        ...answerFields,
        durationSec: Math.round((performance.now() - state.startedAt) / 1000),
      });
      window.location.assign(DESTINATION);
      return;
    }
    // Every screen gets its own history entry, with the URL unchanged.
    window.history.pushState({ lqStep: state.trail.length + 1 }, "");
    state.trail = [...state.trail, next];
    renderStep();
  }

  function answer(question, ids) {
    const position = state.trail.length;
    const nextAnswers = { ...state.answers, [question]: ids };
    // Going back and changing age or concern makes later answers stale: a
    // new age may skip the life-stage question, and a new concern opens a
    // different track whose areas, stages and options don't match.
    const changed = state.answers[question]?.join(",") !== ids.join(",");
    const stale = !changed ? [] : question === "age" ? ["lifeStage"] : question === "concern" ? TRACK_QUESTIONS : [];
    for (const key of stale) delete nextAnswers[key];
    if (stale.length > 0) {
      state.drafts = Object.fromEntries(Object.entries(state.drafts).filter(([key]) => !stale.includes(key)));
    }
    state.answers = nextAnswers;
    track("quiz_answer", { quizId: QUIZ_ID, step: position, question, answer: ids.join(",") });
    return nextAnswers;
  }

  function pickSingle(question, option) {
    if (state.lock || state.settling) return;
    state.lock = true;
    const nextAnswers = answer(question, [option.id]);
    updateSelection();
    window.setTimeout(() => {
      // Each screen change clears the lock, so a Back press during the pause
      // cancels this advance instead of pushing from the wrong screen.
      if (state.lock) goNext(nextAnswers);
    }, AUTO_ADVANCE_MS);
  }

  function toggleMulti(question, option) {
    // Same double-tap guard: a tile tap meant for the last screen must not land on this one.
    if (state.lock || state.settling) return;
    const exclusiveIds = optionsFor(question, state.answers)
      .filter((candidate) => candidate.exclusive)
      .map((candidate) => candidate.id);
    const base = state.drafts[question] ?? state.answers[question] ?? [];
    const nextDraft = base.includes(option.id)
      ? base.filter((id) => id !== option.id)
      : option.exclusive
        ? [option.id]
        : [...base.filter((id) => !exclusiveIds.includes(id)), option.id];
    state.drafts = { ...state.drafts, [question]: nextDraft };
    updateSelection();
  }

  function continueMulti() {
    const v = view();
    if (state.lock || state.settling || v.draft.length === 0) return;
    state.lock = true;
    goNext(answer(v.step, v.draft));
  }

  function continueInfo() {
    if (state.lock || state.settling) return;
    state.lock = true;
    goNext(state.answers);
  }

  root.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest("[data-lq-back]")) {
      window.history.back();
      return;
    }
    const optionButton = target.closest(".lq-step [data-lq-option]");
    if (optionButton) {
      const step = state.trail[state.trail.length - 1];
      const id = optionButton.getAttribute("data-lq-option");
      const option = optionsFor(step, state.answers).find((candidate) => candidate.id === id);
      if (!option) return;
      if (MULTI[step]) toggleMulti(step, option);
      else pickSingle(step, option);
      return;
    }
    const cta = target.closest("[data-lq-cta]");
    if (cta) {
      if (cta.getAttribute("data-lq-cta") === "multi") continueMulti();
      else continueInfo();
    }
  });

  /* ── History ───────────────────────────────────────────────────── */

  function markQuizVisitor() {
    try {
      sessionStorage.setItem(QUIZ_VISITOR_KEY, "1");
    } catch {
      // Storage can be blocked; the popup then behaves as before.
    }
  }

  /** Coming back from the product page or reloading mid-quiz restarts at step 1. */
  function start() {
    // If this entry is a later step, rewind to the quiz's own first entry so
    // Back afterwards returns to the ad or referrer instead of dead steps.
    const stale = window.history.state?.lqStep ?? 0;
    if (stale > 1) window.history.go(-(stale - 1));
    else window.history.replaceState({ ...window.history.state, lqStep: 1 }, "");
    // The product page email popup stays closed for anyone who came through the quiz.
    markQuizVisitor();
    state.startedAt = performance.now();
  }

  window.addEventListener("popstate", (event) => {
    const target = event.state?.lqStep;
    if (!target) return;
    if (target > state.trailLength) {
      // Forward to a screen this visit no longer has: step back to the entry
      // that matches the screen on show, so the next Back works first time.
      window.history.go(state.trailLength - target);
      return;
    }
    if (target < state.trail.length) {
      state.trail = state.trail.slice(0, target);
      renderStep();
    }
  });

  // Back from the product page can restore this page from the back/forward
  // cache mid-funnel; the Next app remounted the quiz there, so start over.
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    window.clearTimeout(settleTimer);
    Object.assign(state, { trail: ["age"], answers: {}, drafts: {}, today: formatToday(), lock: false, trailLength: 1 });
    start();
    renderStep();
  });

  // The section already rendered the first screen; adopt it as is.
  start();
  const first = view();
  renderChrome(first);
  onScreen(first, root.querySelector(".lq-step"));
})();

/* ── p-support.js ── */
/*
 * Support page FAQ search (sections/page-support.liquid), ported from the
 * useMemo filters in src/app/support/page.tsx: matches the question or the
 * answer, case-insensitively; the desktop grid re-splits the matches into
 * two columns (first half rounded up on the left). Rows keep their own
 * open/closed state while filtering (frontend/js/c-faq.js toggles them).
 */
(function () {
  const root = document.querySelector("[data-support]");
  if (!root) return;
  const input = root.querySelector("[data-support-search]");
  const empty = root.querySelector("[data-support-empty]");
  const results = root.querySelectorAll("[data-support-results]");
  const list = root.querySelector("[data-support-list]");
  const columns = root.querySelectorAll("[data-support-column]");
  if (!input || !list || columns.length !== 2) return;

  const text = (el) => (el?.textContent || "").trim().toLowerCase();
  const entry = (row) => ({
    row,
    q: text(row.querySelector("[data-faq-trigger]")),
    a: text(row.querySelector("[data-faq-panel]")),
  });
  const mobileRows = [...list.querySelectorAll("[data-faq-item]")].map(entry);
  const desktopRows = [...columns].flatMap((col) => [...col.querySelectorAll("[data-faq-item]")]).map(entry);

  // A row only renders while it matches; like the old page, emptying the
  // results unmounts the lists, so rows come back closed.
  const close = (row) => {
    row.querySelector("[data-faq-trigger]")?.setAttribute("aria-expanded", "false");
    row.querySelector("[data-faq-chevron]")?.classList.remove("rotate-180");
    const panel = row.querySelector("[data-faq-panel]");
    panel?.classList.remove("grid-rows-[1fr]");
    panel?.classList.add("grid-rows-[0fr]");
  };

  let lastQuery = "";
  const apply = () => {
    const query = input.value.trim().toLowerCase();
    if (query === lastQuery) return;
    lastQuery = query;
    const keep = (rows) =>
      rows.filter((r) => !query || r.q.includes(query) || r.a.includes(query)).map((r) => r.row);
    const mobile = keep(mobileRows);
    const desktop = keep(desktopRows);
    const none = mobile.length === 0;

    empty.hidden = !none;
    results.forEach((el) => (el.hidden = none));
    if (none) {
      mobileRows.concat(desktopRows).forEach((r) => close(r.row));
    }
    list.replaceChildren(...mobile);
    const mid = Math.ceil(desktop.length / 2);
    columns[0].replaceChildren(...desktop.slice(0, mid));
    columns[1].replaceChildren(...desktop.slice(mid));
  };

  input.addEventListener("input", apply);
  // Restored form state (back/forward cache, autofill) can prefill the box.
  window.addEventListener("pageshow", apply);
  apply();
})();

/* ── p-track.js ── */
/*
 * Track page (sections/page-track.liquid). The old page POSTed the order
 * number and email to /api/shopify/track-order and drew a timeline. Shopify
 * has no such endpoint for themes, so after the same validation and loading
 * state the shopper goes to the customer account (form action =
 * routes.account_url), where Shopify lists orders with live tracking. The
 * typed values are never sent anywhere.
 */
(function () {
  const form = document.querySelector("[data-track-form]");
  if (!form) return;
  const orderInput = form.querySelector("[data-track-order]");
  const emailInput = form.querySelector("[data-track-email]");
  const error = form.querySelector("[data-track-error]");
  const button = form.querySelector("[data-track-submit]");
  const idle = form.querySelector("[data-track-idle]");
  const loading = form.querySelector("[data-track-loading]");

  const setError = (message) => {
    error.textContent = message;
    error.hidden = !message;
  };
  const setLoading = (on) => {
    button.disabled = on;
    idle.hidden = on;
    loading.hidden = !on;
  };

  // Typing clears the error, like the old useEffect on both fields.
  [orderInput, emailInput].forEach((input) =>
    input.addEventListener("input", () => {
      if (!error.hidden) setError("");
    }),
  );

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (!orderInput.value.trim() || !emailInput.value.trim()) {
      setError("Please enter both your order number and email address.");
      return;
    }
    setError("");
    setLoading(true);
    window.location.assign(form.getAttribute("action") || window.Lavendr?.routes?.account || "/account");
  });

  // Coming back with the back button restores the page from cache mid-load.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) setLoading(false);
  });
})();
